// Typed client for admin-api (docs/admin-api.md). Hand-written for now; a
// generated SDK from contracts/api/openapi.yaml can replace it later without
// touching the pages, which only import from here.
//
// No business logic lives in the console: every rule (roles, market scope,
// four-eyes thresholds, reasons) is enforced by admin-api. This file only
// shapes requests and responses.

import { surfaceUrl } from "./env";
import type { Arc, Directive, DryDirective, EnvelopeBody } from "./pages/director/logic";
import { normalizeOverview, type AutopilotOverview } from "./pages/director/autopilotLogic";
import type { CityEventRow, CityPostRow, CityReviewRow, CivicView, Edition, TimesView } from "./pages/city/logic";
import type { BanditStat, GenLevelRow } from "./pages/director/genLogic";
import type { ContestsView } from "./pages/city/contestsLogic";

export const API_URL: string = surfaceUrl("admin-api", import.meta.env.VITE_ADMIN_API_URL as string | undefined, "http://localhost:8083");

// ---------------------------------------------------------------- session token

const TOKEN_KEY = "softlife.admin.token";
let token: string | null = readStored();
let onUnauthorized: ((code: string) => void) | null = null;

function readStored(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  return token;
}

export function setToken(t: string | null) {
  token = t;
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage blocked: the token stays in memory only */
  }
}

/** Called when admin-api answers 401 (session ended, idle for 12h, revoked). */
export function setUnauthorizedHandler(fn: ((code: string) => void) | null) {
  onUnauthorized = fn;
}

// ---------------------------------------------------------------- errors

export class ApiError extends Error {
  status: number;
  code: string;
  retryable: boolean;
  details?: unknown;
  constructor(status: number, code: string, message: string, retryable = false, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.retryable = retryable;
    this.details = details;
  }
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message || e.code;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ---------------------------------------------------------------- transport

type Query = Record<string, string | number | boolean | undefined | null>;

function qs(q?: Query): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? "?" + s : "";
}

async function raw(method: string, path: string, body?: unknown, query?: Query): Promise<Response> {
  const headers: Record<string, string> = {};
  // A Blob (an uploaded image) goes as-is with its own type; anything else is JSON.
  const blob = typeof Blob !== "undefined" && body instanceof Blob;
  if (body !== undefined) headers["Content-Type"] = blob ? (body as Blob).type || "application/octet-stream" : "application/json";
  if (token) headers["Authorization"] = "Bearer " + token;
  let res: Response;
  try {
    res = await fetch(API_URL + path + qs(query), {
      method,
      headers,
      body: body === undefined ? undefined : blob ? (body as Blob) : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "Can't reach admin-api at " + API_URL + ". Check the VPN / proxy and that the service is running.", true);
  }
  if (!res.ok) {
    let code = "http_" + res.status;
    let message = res.statusText || "Request failed";
    let retryable = res.status >= 500;
    let details: unknown;
    try {
      const j = (await res.json()) as { error?: { code?: string; message?: string; retryable?: boolean; details?: unknown } };
      if (j.error) {
        code = j.error.code || code;
        message = j.error.message || message;
        retryable = j.error.retryable ?? retryable;
        details = j.error.details;
      }
    } catch {
      /* non-JSON error body */
    }
    // A failed sign-in (password-less or not) isn't an ended session.
    if (res.status === 401 && !path.startsWith("/admin/v1/auth/")) {
      setToken(null);
      onUnauthorized?.(code);
    }
    throw new ApiError(res.status, code, message, retryable, details);
  }
  return res;
}

async function req<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const res = await raw(method, path, body, query);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(path: string, query?: Query) => req<T>("GET", path, undefined, query);
const post = <T>(path: string, body: unknown = {}, query?: Query) => req<T>("POST", path, body, query);
const put = <T>(path: string, body: unknown) => req<T>("PUT", path, body);
const del = <T>(path: string, query?: Query) => req<T>("DELETE", path, undefined, query);
const enc = encodeURIComponent;
const receiptName = (number: string | null, id: string) => (number || id).replace(/[^A-Za-z0-9._-]+/g, "-");

/** Fetches a file with the bearer token and hands it to the browser as a download. */
export async function download(path: string, filename: string, query?: Query): Promise<void> {
  const res = await raw("GET", path, undefined, query);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Fetches an image with the bearer token as an object URL (an <img> cannot send the header). The caller revokes it. */
export async function imageUrl(path: string): Promise<string> {
  const res = await raw("GET", path);
  return URL.createObjectURL(await res.blob());
}

/**
 * Fetches a page with the bearer token and opens it in a new tab (receipts).
 * The tab is opened before the fetch, inside the click, so popup blockers let it through.
 */
export async function openInTab(path: string, query?: Query): Promise<void> {
  const tab = window.open("", "_blank");
  if (tab) tab.opener = null;
  try {
    const res = await raw("GET", path, undefined, query);
    const blob = await res.blob();
    const url = URL.createObjectURL(new Blob([blob], { type: res.headers.get("Content-Type") || "text/html" }));
    if (tab) tab.location.href = url;
    else window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

// ---------------------------------------------------------------- shared types

/** A flat SQL row as admin-api returns it (snake_case columns). */
export type Row = Record<string, unknown>;

// In-app feedback (ADR 0098)
export interface FeedbackRowT {
  id: string; ref: string; kind: string; status: string; snippet: string; page: string; created_at: ISO; updated_at: ISO;
  name: string; market: string; account_kind: string; images: number; assignee_name: string;
}
export interface FeedbackQueue { feedback: FeedbackRowT[]; counts: { new: number; triaged: number; planned: number; done: number; open_bugs: number }; can_act: boolean }
export interface FeedbackDetail {
  feedback: {
    id: string; ref: string; kind: string; status: string; message: string; page: string; context: { build?: string; device?: string; screen?: string; market?: string; kind?: string };
    staff_note: string; reply: string; created_at: ISO; updated_at: ISO; actioned_at: ISO | null; account_id: string | null; name: string; market: string; account_kind: string; assignee_name: string;
  };
  images: { id: string; content_type: string; width: number; height: number; bytes: number }[];
  can_act: boolean;
  statuses: Record<string, string>;
}
export type ISO = string;

// ---------------------------------------------------------------- support tickets (ADR 0093)

export interface TicketFilter { status?: string; category?: string; assignee?: string; overdue?: string; q?: string; limit?: number }
export interface TicketRowT {
  id: string; ref: string; account_id: string; name: string; market: string; category: string; priority: "low" | "normal" | "high" | "urgent";
  status: string; assignee: string | null; assignee_name: string | null; due_at: ISO | null; overdue: boolean; first_response_at: ISO | null;
  resolved_at: ISO | null; outcome: string | null; created_at: ISO; updated_at: ISO; topic: string; snippet: string; player_messages: number; last_by: "player" | "staff";
}
export interface TicketQueue {
  tickets: TicketRowT[];
  counts: { active: number; new: number; mine: number; unassigned: number; waiting: number; overdue: number };
  can_act: boolean;
}
export interface TicketMessageT { id: string; author_kind: "player" | "staff" | "system"; internal: boolean; body: string; created_at: ISO; staff_name: string }
/** A picture a player attached. Sizes only: it is loaded when a staff member clicks it (openTicketPicture). */
export interface TicketPictureT { id: string; message_id: string; content_type: string; bytes: number; width: number; height: number; created_at: ISO; scanner: string; erased: boolean }
/** What the ticket view shows about the payment (payment tickets only). `error` is set when the payments service could not be reached. */
export interface TicketPayment {
  error?: string;
  order: { order_id: string; kind: string; pack_key: string; status: string; amount_minor: number; currency: string; provider: string; method: string; provider_ref?: string; created_at: ISO; paid_at: ISO | null; granted_at: ISO | null; refunded_minor: number; play_amount: number };
  delivery: { action: string; seq: string; status: string; attempts: number; last_error?: string }[];
  disputes: { id: string; status: string; amount_minor: number; currency: string; deadline: ISO | null; reason: string | null }[];
  open_dispute: boolean;
  wallet_credit: { entry_id: string; amount: number; created_at: ISO }[];
  wallet_reversals: { entry_id: string; amount: number; created_at: ISO; reverses: string | null }[];
  wallet_balance: number;
  purchase_hold: boolean;
  purchase_hold_by: string | null;
  voluntary_refunds: number;
  voluntary_refund_limit: number;
  can_redeliver: boolean;
  can_refund: boolean;
  can_grant: boolean;
  can_evidence: boolean;
}
export interface TicketLinked { id: string; ref: string; category: string; status: string; outcome: string | null }
export interface TicketDetail {
  ticket: TicketRowT & { kind: string; account_status: string; closed_at: ISO | null; close_reason?: string | null; order_id: string | null; details: Record<string, unknown> | null; context: Row | null; message: string };
  messages: TicketMessageT[];
  assignees: { id: string; name: string }[];
  can_act: boolean;
  category_label: string;
  outcomes: string[];
  me: string;
  attachments?: TicketPictureT[];
  files_on?: boolean;
  linked?: TicketLinked[];
  is_dispute?: boolean;
  payment?: TicketPayment;
  can_evidence?: boolean;
}
export interface TicketMacro { key: string; title: string; body: string; category: string | null; outcome: string | null }

/** Result of any write that goes through the action model. */
export type ActionResult =
  | { status: "done"; result: unknown }
  | { status: "pending_approval"; approval_id: string; message?: string }
  | { status: string; result?: unknown; approval_id?: string; message?: string };

export const isPending = (r: ActionResult | undefined | null): r is { status: "pending_approval"; approval_id: string; message?: string } =>
  !!r && r.status === "pending_approval";

/** role → markets ([] = all markets). */
export type Grants = Record<string, string[]>;
/** permission → markets (["*"] = all markets). */
export type Permissions = Record<string, string[]>;

export interface Staff {
  id: string;
  email: string;
  name: string;
  roles: Grants;
}

export interface Me {
  staff: Staff;
  permissions: Permissions;
  env: string;
}

/** What a passkey ceremony starts with (see src/webauthn.ts). */
export interface PasskeyCeremony {
  ceremony: string;
  options: import("./webauthn").Options;
}

export interface Passkey {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
  /** Backed up and shared across the owner's devices (iCloud Keychain, Google Password Manager). */
  synced: boolean;
}

export interface InviteInfo {
  email: string;
  name: string;
  roles: string[];
  expires_at: string;
}

export interface LoginResult {
  token: string;
  staff: Staff;
  permissions: Permissions;
  idle_hours: number;
}

export interface PlayerRow {
  id: string;
  name: string;
  market: string;
  kind: string;
  status: string;
  created_at: ISO;
  level: number | null;
  balance: number | null;
  funded_minor: number | null;
  pass: boolean | null;
}

export interface Account {
  id: string;
  name: string;
  market: string;
  kind: string;
  status: string;
  status_reason: string | null;
  timezone: string;
  created_at: ISO;
  name_changed_at: ISO | null;
  has_dob: boolean;
}

export interface OrderRow {
  id: string;
  account_id: string;
  market: string;
  kind: string;
  pack_key: string | null;
  amount_minor: number;
  currency: string;
  tax_minor: number;
  play_amount: number;
  provider: string;
  method: string;
  provider_ref?: string | null;
  status: string;
  refunded_minor: number;
  created_at: ISO;
  paid_at: ISO | null;
  granted_at: ISO | null;
}

export interface PlayerProfile {
  /** Staff switched off payments between players for this account (econ.p2p_block, ADR 0021). */
  p2p_block?: { reason: string; blocked_by: string; created_at: string } | null;
  account: Account | null;
  progress: Row | null;
  wallet: { balance: number; locked: boolean; updated_at: ISO } | null;
  identities: { kind: string; verified_at: ISO | null; created_at: ISO }[];
  sessions: { id: string; platform: string; created_at: ISO; last_seen_at: ISO; revoked_at: ISO | null }[];
  devices: { id: string; platform: string; first_seen_at: ISO; accounts_on_device: number }[];
  assets: { id: string; kind: string; item_key: string; name: string; price_paid: number; debt: number; created_at: ISO }[];
  crew: { id: string; name: string; code: string; role: string; joined_at: ISO } | null;
  parties: { id: string; code: string; title: string; status: string; cost: number; collected: number; ends_at: ISO }[];
  badges: { badge: string; created_at: ISO }[];
  notes: { id: string; body: string; created_at: ISO; staff: string }[];
  flags: { key: string; value: unknown; updated_at: ISO }[];
  risk_signals: { id: string; kind: string; score: number; detail: unknown; created_at: ISO }[];
  limits: Row | null;
  tickets: { id: string; topic: string; status: string; created_at: ISO }[];
  orders?: OrderRow[];
  /** Look, interests and bio (null until the player saves one). */
  profile?: {
    avatar: { body: string; skin: string; hair: string; hair_color: string; outfit: string; accessory: string } | null;
    interests: string[] | null;
    bio: string;
    intro_seen_at: ISO | null;
    updated_at: ISO;
  } | null;
  /** First-week checklist steps done (XP only). */
  onboarding?: { step: string; done_at: ISO; claimed_at: ISO | null }[];
  location?: { city: string; since: ISO } | null;
  social?: { friends: number; blocked_by: number; hidden_messages: number; reports_against: number } | null;
  /** Age assurance (ADR 0092), read-only: whether they are a verified adult now and the last checks. */
  age_check?: {
    verified: boolean;
    latest_status: string;
    checks: { id: string; method: string; provider: string; provider_ref: string; status: string; over_age: number; created_at: ISO; decided_at: ISO | null; expires_at: ISO | null; decided_by: string | null }[];
    terms?: { document: string; version: string; accepted_at: ISO }[];
  };
  /** NPC love interests (ADR 0092, R1), read-only: where they stand with each person. Closeness is not sent. Staff reset with romance.reset. */
  romance?: { npc: string; stage: string; paused: boolean; ended_from: string | null; talks: number; created_at: ISO; stage_at: ISO; last_at: ISO }[];
  /** Date-of-birth correction (ADR 0094), read-only: the open request and the last ten. Never the date asked for (player.reveal shows it, logged). */
  dob_correction?: DobCorrectionPanel;
}

export interface DobRequest {
  id: string;
  status: "awaiting_check" | "pending" | "scheduled" | "applied" | "declined" | "cancelled" | "expired";
  direction: "lower" | "raise" | "raise_cross";
  band_from: string;
  band_to: string;
  years_delta: number;
  /** 13 or 18 when the request crosses an age line upward (it needs a provider check for that age), else 0. */
  check_age: number;
  check_status: string;
  evidence_ok?: boolean;
  /** The player's own words. Not the date. */
  reason: string;
  vendor_ref: string;
  created_at: ISO;
  target_at: ISO;
  deadline_at: ISO;
  overdue: boolean;
  past_deadline: boolean;
  disputed: boolean;
  apply_after: ISO | null;
  applied_at: ISO | null;
  decided_at: ISO | null;
  reason_code: string;
  decline_reasons: string;
  ticket_id: string | null;
}

export interface DobCorrectionPanel {
  open: DobRequest | null;
  requests: DobRequest[];
  /** An upward correction has been made before, so a second needs an owner. */
  prior_upward: boolean;
}

export interface LedgerEntry {
  id: string;
  amount: number;
  balance_after: number;
  kind: string;
  reason: string;
  source_type: string | null;
  source_id: string | null;
  created_at: ISO;
}

export interface Ledger {
  entries: LedgerEntry[];
  integrity: { stored: number; replayed: number; ok: boolean };
}

export interface Approval {
  id: string;
  action: string;
  payload: unknown;
  reason: string;
  market: string | null;
  status: string;
  result: unknown;
  created_at: ISO;
  decided_at: ISO | null;
  requested_by: string;
  decided_by: string | null;
  mine: boolean;
  /** Your own request, and you're an owner: you may approve it yourself. */
  can_self_approve?: boolean;
  /** The permission that decides this one, when it is not approvals.decide (a date-of-birth correction: players.age). */
  decide_perm?: string;
  /** Facts the page shows for an action whose payload names only a record (a date-of-birth correction). */
  context?: Record<string, unknown>;
}

export interface AuditEntry {
  seq: number;
  staff_id: string | null;
  staff: string;
  action: string;
  target: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
  ip: string | null;
  at: ISO;
}

export interface OrderDetail {
  order: OrderRow & { payment_ref?: string | null; catalogue_version?: number; checkout?: unknown };
  events?: { event_id: string; provider: string; event_type: string; received_at: ISO; processed_at: ISO | null; error: string | null }[];
  journal?: { memo: string; occurred_at: ISO; account_code: string; debit_minor: number; credit_minor: number; currency: string }[];
  api_calls: { id: string; action: string; seq: number; status: string; attempts: number; last_error: string | null; created_at: ISO; done_at: ISO | null }[];
  disputes: { id: string; status: string; amount_minor: number; currency: string; deadline: ISO | null; reason: string | null }[];
}

export interface JournalLine {
  entry_id: string;
  occurred_at: ISO;
  memo: string;
  source_type: string;
  source_id: string;
  account_code: string;
  debit_minor: number;
  credit_minor: number;
  currency: string;
}

export interface RevenueRow {
  market: string;
  currency: string;
  provider?: string;
  /** by=day rows carry the day in an unnamed column (see BACKEND_ISSUES.md). */
  date_trunc?: ISO;
  day?: ISO;
  orders: number;
  payers: number;
  passes: number;
  gross_minor: number;
  tax_minor: number;
  refunded_minor: number;
  net_minor: number;
  chargebacks: number;
}

export interface Provider {
  provider: string;
  configured: boolean;
  fake: boolean;
  pending: number;
  paid_24h: number;
}

export interface SettlementImport {
  provider: string;
  settlement_date: string;
  provider_ref: string;
  currency: string;
  lines: { reference: string; gross_minor: number; fee_minor: number }[];
}

export interface CatalogueVersionRow {
  version: number;
  status: string;
  notes: string | null;
  created_by: string | null;
  approved_by: string | null;
  created_at: ISO;
  published_at: ISO | null;
}

export interface CatalogueBody {
  market: string;
  version: number;
  name?: string;
  city?: string;
  city_key?: string;
  currency: string;
  symbol: string;
  play_symbol: string;
  packs: { key: string; pay_minor: number; play: number; bonus_pct: number; label: string }[];
  economy?: { pack2_play: number } & Row;
  methods?: { key: string; name: string; note: string; provider: string }[];
  pulse?: PulseEvent[];
  [k: string]: unknown;
}

export interface CatalogueVersion {
  body: CatalogueBody;
  invariants: string[] | null;
  /** Typical free daily income (jobs at the average wage), % of Pack 2. */
  free_daily_pct: number;
  /** The most a player can earn free a day (best-paying jobs), % of Pack 2: what the cap and ceiling hold. */
  free_daily_max_pct?: number;
  pass_daily_pct: number;
}

export interface SimCheck {
  name: string;
  value: number;
  min?: number;
  max?: number;
  pass: boolean;
  skipped?: boolean;
  why: string;
}

export interface SimArchetype {
  name: string;
  payer: boolean;
  agents: number;
  active_day_share: number;
  dry_rate: number;
  free_income_pct: number;
  asset_income_pct: number;
  cup_pct: number;
  median_balance: number;
  p90_balance: number;
  topups: number;
  revenue_minor: number;
  first_hustle?: { median_days: number; reached: number };
  first_car?: { median_days: number; reached: number };
  first_property?: { median_days: number; reached: number };
  [k: string]: unknown;
}

export interface DraftResult {
  version: number;
  invariants: string[] | null;
  sim_error?: string;
  sim?: {
    pass: boolean;
    checks: SimCheck[];
    warnings: string[] | null;
    archetypes: SimArchetype[];
    revenue: { total_minor: number; packs_minor: number; pass_minor: number; topups: number; payers: number; arppu_minor: number; arpu_minor: number; per_payer_month_minor: number };
  };
}

export interface DiffChange {
  path: string;
  from: unknown;
  to: unknown;
}

// ---------------------------------------------------------------- free play (ADR 0018)

export type FreePreset = "standard" | "relaxed" | "generous" | "custom";

/** Free-income sources in the preview (string: admin-api may add more). */
export type FreeSource = "pocket" | "missions" | "ads" | "quests" | "challenges" | "jobs" | "career" | "ops" | (string & {});

/** economy.free: the generosity and complexity dials. */
export interface FreeDials {
  preset: FreePreset;
  mult: number;
  pocket_mult: number;
  mission_mult: number;
  quest_mult: number;
  challenge_mult: number;
  ad_mult: number;
  cup_mult: number;
  job_mult: number;
  /** Career wages (ADR 0022); the presets keep mult × salary_mult near 1 / 1.2 / 1.3. */
  salary_mult: number;
  /** Co-op op takes (ADR 0025), like salary_mult for wages: 1 / 0.8 / 0.6 in the presets. */
  op_mult: number;
  /** The most op takes pay a player in 7 days, % of Pack 2 before the dials (code ceiling 20). */
  op_weekly_pct: number;
  /** The most story beats pay a day, % of Pack 2 (code ceiling 5, ADR 0026). */
  story_daily_pct: number;
  guest_mult: number;
  ads_per_day_app: number;
  ads_per_day_web: number;
  shifts_per_day: number;
  job_ttl_minutes: number;
  difficulty: number;
  full_only_above: number;
}

export interface FreeJobStep {
  event: string;
  goal: number;
  label: string;
  field?: string;
  min?: number;
}

export interface FreeJob {
  key: string;
  name: string;
  desc: string;
  icon: string;
  steps: FreeJobStep[];
  wage_pct: number;
  min_level: number;
  cooldown_minutes: number;
  full_only: boolean;
}

export interface FreePreview {
  market: string;
  preset: string;
  free_daily_pct: number;
  with_pass_pct: number;
  free_daily_play: number;
  /** The most a player can earn free a day (shifts on the best-paying jobs): what the cap, the ceiling and boost_cap hold. */
  free_daily_max_pct: number;
  free_daily_max_play: number;
  /**
   * pct/play are typical; jobs (best-paying jobs), career (best rung, merit raises, bonus) and ops (the whole weekly
   * allowance ÷ 7; typical is half of it, ADR 0025) also carry max_pct/max_play.
   */
  by_source: { source: FreeSource; pct: number; play: number; max_pct?: number; max_play?: number }[];
  guest_daily_pct?: number;
  pack2_play: number;
  cap_pct: number;
  ceiling_pct: number;
  band: [number, number];
  in_band: boolean;
  boost_cap: number;
  invariants: string[] | null;
  sim?: { pass: boolean; preset: string; checks: SimCheck[]; warnings: string[] | null; thresholds: Record<string, number>; archetypes: SimArchetype[] };
  sim_error?: string;
}

// ---------------------------------------------------------------- real-world signals (ADR 0023)

export interface SignalHoliday { day: string; name: string; kind: string; off: boolean; confirmed: boolean; removed: boolean; source: string; updated_by: string | null; updated_at: string }
export interface SignalFixture {
  id: string; competition: string; home: string; away: string; kickoff_at: string; status: string;
  score_home: number | null; score_away: number | null; source: string; featured: boolean; event_id: string | null; created_by: string | null;
}
export interface SignalsView {
  market: string;
  weather: { city: string; weather: string; temp_c: number; wind_ms: number; humidity: number | null; source: string; observed_at: string; fetched_at: string }[];
  holidays: SignalHoliday[];
  fixtures: SignalFixture[];
  providers: { provider: string; ok_at: string | null; fail_at: string | null; fails: number; last_error: string }[];
  flags: { key: string; value: unknown }[];
}

// ---------------------------------------------------------------- co-op ops (ADR 0025)

export type OpRunStatus = "lobby" | "live" | "done" | "failed" | "abandoned" | "cancelled";
/** Why a player's take was or wasn't paid in full (string: admin-api may add more). */
export type OpTakeReason = "paid" | "allowance" | "daily_limit" | "linked" | "offline" | "left" | "failed" | (string & {});
/** Sums come from SQL numeric columns and may arrive as strings. */
type Num = number | string;

export interface OpTake {
  account: string;
  take: Num;
  /** What was minted: the take, less what the allowance, daily limit or linked-account rule held back. */
  paid: Num;
  reason: OpTakeReason;
}

export interface OpRun {
  id: string;
  market: string;
  op_key: string;
  status: OpRunStatus | (string & {});
  public: boolean;
  phase: number;
  pressure: number;
  /** One score per closed phase. */
  scores: number[] | null;
  stars: number | null;
  leader_id: string;
  leader: string;
  created_at: ISO;
  started_at: ISO | null;
  ended_at: ISO | null;
  players: Num;
  paid: Num;
  held_back: Num;
  takes: OpTake[] | null;
}

/** The last 7 days per market and op. */
export interface OpSummary {
  market: string;
  op_key: string;
  runs: Num;
  done: Num;
  failed: Num;
  avg_stars: Num | null;
  paid: Num;
}

/** An ops_task_fast risk signal: a task answered faster than a person could. */
export interface OpFastSignal {
  account_id: string;
  name: string;
  market: string;
  detail: unknown;
  created_at: ISO;
}

export type OpPlanStatus = "planning" | "done" | "failed" | "expired" | "cancelled";

/** A Big Op planning board: setups run first, then the scheduled finale. */
export interface OpPlanRow {
  id: string;
  market: string;
  op_key: string;
  status: OpPlanStatus | (string & {});
  /** Play money the leader paid to open the board (burned, not minted back). */
  setup_cost: Num;
  /** Setup keys finished so far (never null after api.ops). */
  setups_done: string[];
  /** Agreed finale cut per account, in percent. */
  cuts: Record<string, Num> | null;
  finale_at: ISO | null;
  expires_at: ISO | null;
  created_at: ISO;
  leader_id: string;
  leader: string;
  members: Num;
}

export interface OpsView {
  runs: OpRun[];
  summary: OpSummary[];
  fast_signals: OpFastSignal[];
  /** Big Op plans, newest first. */
  plans: OpPlanRow[];
}

// ---------------------------------------------------------------- careers (ADR 0022)

/** world.careers: how shifts run. Shares (shift_pay_share, bonus_pct, merit_step) are fractions. */
export interface CareersCfg {
  enabled: boolean;
  slots: ShiftSlot[];
  clock_in_early_min: number;
  clock_in_late_min: number;
  late_after_min: number;
  clock_out_grace_min: number;
  beat_window_min: number;
  beats_per_shift: number;
  work_days_per_week: number;
  sick_days_per_week: number;
  shift_pay_share: number;
  payday_weekday: number;
  payday_hour: number;
  bonus_pct: number;
  bonus_perf: number;
  merit_step: number;
  merit_steps: number;
  needs?: {
    energy_regen_h: number;
    sleep_regen_h: number;
    sleep_from: number;
    sleep_to: number;
    mood_rest: number;
    mood_drift_h: number;
    low_energy: number;
    restores?: Record<string, { energy?: number; mood?: number }>;
    daily_cap?: { energy?: number; mood?: number };
  };
}

export interface ShiftSlot {
  key: string;
  label: string;
  start: string;
  end: string;
  collar: string;
}

export interface Employer {
  key: string;
  name: string;
  sector: string;
  collar: string;
  desc: string;
  icon: string;
  workplaces: string[] | null;
}

export interface Workplace {
  key: string;
  employer?: string;
  city: string;
  district: string;
  kind: string;
  interior: string;
  brandable?: boolean;
  sector?: string;
}

export interface BeatSpec {
  kind: "event" | "mini" | (string & {});
  event?: string;
  goal?: number;
  field?: string;
  min?: number;
  mini?: string;
  label: string;
}

export interface Rung {
  key: string;
  title: string;
  wage_pct: number;
  requires: { level?: number; reputation?: number; shifts?: number; certs?: string[]; full_only?: boolean };
  beats: BeatSpec[] | null;
  promote: { shifts: number; avg_perf: number };
  energy_cost: number;
  mood_cost: number;
}

export interface Ladder {
  key: string;
  name: string;
  collar: string;
  sector: string;
  rungs: Rung[];
}

export interface Position {
  key: string;
  employer: string;
  workplace: string;
  ladder: string;
  entry_rung: number;
  max_rung: number;
  slots: string[] | null;
  interview?: string[] | null;
}

/** GET /admin/v1/players/{id}/work: rows straight from the work schema. */
export interface PlayerWork {
  employment: {
    id: string;
    employer_ref: string;
    position_key: string;
    workplace: string;
    ladder: string;
    sector: string;
    rung: number;
    merit: number;
    slot: string;
    workdays: number; // bit 0 = Sunday … bit 6 = Saturday
    tz: string;
    status: string;
    shifts_total: number;
    shifts_at_rung: number;
    streak: number;
    best_streak: number;
    warnings: number;
    started_at: ISO;
    ended_at: ISO | null;
    end_reason: string | null;
  }[];
  shifts: {
    id: string;
    employment_id: string;
    local_day: ISO;
    starts_at: ISO;
    ends_at: ISO;
    status: string;
    clock_in_at: ISO | null;
    clock_out_at: ISO | null;
    perf: number | null;
    wage: number;
    paid_now: number;
    payslip_id: string | null;
  }[];
  payslips: {
    id: string;
    employment_id: string;
    week: ISO;
    shifts: number;
    perf_avg: number | string | null;
    gross: number;
    paid_now: number;
    bonus: number;
    amount: number;
    created_at: ISO;
  }[];
  experience: { sector: string; shifts: number; best_rung: number }[];
}

export interface FreePlay {
  market: string;
  version: number;
  free: FreeDials;
  jobs: FreeJob[];
  free_income_cap_pct: number;
  preview: FreePreview;
  presets: Record<string, FreeDials>;
  bands: Record<string, [number, number]>;
  caps: Record<string, number>;
  job_events: { key: string; label: string }[];
  fields: string[];
  icons: string[];
}

export interface FreeProposal {
  market: string;
  free: FreeDials;
  jobs: FreeJob[];
  free_income_cap_pct: number;
  sim?: boolean;
  notes?: string;
}

export interface FreeDrafted {
  version: number;
  invariants: string[] | null;
  sim?: DraftResult["sim"];
  sim_error?: string;
  preview: FreePreview;
}

// ---------------------------------------------------------------- world tuning (ADR 0059)

/** One number a market can set under world.tuning (arms prices, mission, incident and service rewards). */
export interface TuningKnob {
  /** The JSON path under world.tuning, e.g. arms.weapons.sidearm.price_pct. */
  key: string;
  group: "arms" | "jobs" | "incidents" | "services";
  label: string;
  unit: string;
  default: number;
  /** What the game reads now (the override, held to the ceiling and floor). */
  value: number;
  min: number;
  max: number;
  overridden: boolean;
  /** The value in the market's play money, for a share of Pack 2. */
  play?: number;
  note?: string;
}

export interface TuningChange {
  key: string;
  label: string;
  from: number;
  to: number;
}

export interface TuningRejected {
  market: string;
  version: number;
  serving: number;
  errors: string[];
}

export interface WorldTuning {
  market: string;
  version: number;
  pack2_play: number;
  knobs: TuningKnob[];
  tuning: Record<string, unknown> | null;
  rejected?: TuningRejected | null;
}

export interface TuningProposal {
  market: string;
  tuning: Record<string, unknown> | null;
  sim?: boolean;
  notes?: string;
}

export interface TuningPreview {
  market: string;
  pack2_play: number;
  knobs: TuningKnob[];
  tuning: Record<string, unknown> | null;
  changes: TuningChange[];
  tuning_errors: string[];
  invariants: string[];
  sim?: FreePreview["sim"];
  sim_error?: string;
}

export interface TuningDrafted {
  version: number;
  invariants: string[] | null;
  sim?: DraftResult["sim"];
  sim_error?: string;
  changes: TuningChange[];
  knobs: TuningKnob[];
}

export interface EconomyHealth {
  market: string;
  since: ISO;
  flows: { day: ISO; kind: string; credit: number | null; debit: number | null; entries: number }[];
  sinks: { store: string; spent: number; purchases: number }[];
  supply: { wallets: number; supply: number; median_balance: number; p90_balance: number; locked_wallets: number; escrow: number };
  pack2_play: number;
}

export interface MarketLaunch {
  market: string;
  name: string;
  live: boolean;
  source: "flag" | "default";
  default: boolean;
}

export interface Flag {
  key: string;
  value: unknown;
  updated_by: string | null;
  updated_at: ISO;
}

// The assistant (ADR 0089): plans the model proposed from a staff member's words, confirmed step by step.
export type AssistantPlanStatus = "proposed" | "running" | "done" | "partial" | "failed" | "discarded" | "expired" | "interrupted";
export interface AssistantStep {
  id: string;
  tool: string;
  params: Record<string, unknown>;
  summary: string;
  risk?: "low" | "medium" | "high" | string;
  needs_approval: boolean;
  ok: boolean;
  error?: string;
}
export interface AssistantResult {
  step_id: string;
  status: "done" | "pending_approval" | "failed" | "skipped";
  approval_id?: string;
  message?: string;
}
export interface AssistantPlan {
  id: string;
  request: string;
  thread?: { role: "user" | "assistant"; text: string }[];
  answer: string;
  clarification: string;
  steps: AssistantStep[];
  model: string;
  status: AssistantPlanStatus;
  results?: AssistantResult[];
  created_at: ISO;
  expires_at: ISO;
}
export interface AssistantList {
  available: boolean;
  model: string;
  provider: string;
  tools: { name: string }[];
  plans: AssistantPlan[];
}

// Assistant settings (ADR 0090): GET and PUT /admin/v1/assistant/settings (owner only). The key is write-only.
export interface AssistantSettingsView {
  settings: {
    provider: "anthropic" | "openai_compatible";
    model: string | null;
    base_url: string | null;
    daily_usd: number | null;
    price_in_per_mtok: number | null;
    price_out_per_mtok: number | null;
  };
  key: { set: boolean; last4: string; environment: boolean };
  can_store_key: boolean;
  effective: { provider: string; model: string; base_url: string; daily_usd: number; key_source: string; available: boolean; problem: string };
  models: string[];
}

// Switches (ADR 0061): GET /admin/v1/switches and PUT /admin/v1/switches/{key}.
export type SwitchState = "on" | "off" | "inherit";
export interface SwitchSetting {
  market?: string;
  value: unknown;
  /** The value read as on or off; null for a number or JSON. */
  on: boolean | null;
  updated_by: string;
  updated_at: ISO | null;
  reason: string;
  approved_by?: string;
}
export interface SwitchPendingChange {
  approval_id: string;
  /** "" = the platform's switch. */
  market: string;
  change: SwitchState;
  requested_by: string;
  requested_at: ISO;
  reason: string;
  mine: boolean;
  can_decide: boolean;
  needs: string;
}
export interface SwitchRow {
  key: string;
  area: string;
  name: string;
  description: string;
  kind: "feature" | "kill" | "setting";
  scope: "platform" | "platform_market" | "market";
  value_type: "bool" | "number" | "object" | "maintenance";
  rule: "" | "p2p" | "risk" | "llm" | "autopublish";
  risk: "low" | "medium" | "high";
  default: boolean;
  sign_off: string;
  adult: boolean;
  managed_in: string;
  permission: string;
  /** False for a market-only switch: nothing to inherit from, so Off is how a market is cleared. */
  supports_inherit: boolean;
  approval: { on: boolean; off: boolean; clear: boolean; market_on: boolean; market_off: boolean; market_clear: boolean };
  dev_seed: boolean;
  can_platform: boolean;
  can_markets: string[];
  platform: SwitchSetting | null;
  overrides: SwitchSetting[];
  pending: SwitchPendingChange[];
}
export interface SwitchList {
  areas: { key: string; name: string }[];
  markets: { market: string; name: string; live: boolean }[];
  switches: SwitchRow[];
}
export type SwitchResult = { status: "done"; key: string; state: string } | { status: "pending_approval"; approval_id: string; key: string; state: string; message?: string };

export interface PulseEvent {
  key: string;
  name: string;
  desc: string;
  fx: Record<string, number>;
  weight: number;
}

export interface PulseDay {
  day: ISO;
  event_key: string;
  name: string;
  descr: string;
  effects: Record<string, number>;
  scheduled_by: string | null;
}

export interface IndexData {
  index: { item_key: string; day: ISO; value: number }[];
  overrides: { item_key: string; value: number | null; reason: string; set_by: string; active: boolean; created_at: ISO }[];
}

export interface Cup {
  id: string;
  kind: string;
  day: ISO;
  status: string;
  entries: number;
  top: { name: string; best: number }[] | null;
}

export interface ContentVersionRow {
  version: number;
  status: string;
  lint_issues: number;
  created_by: string;
  reviewed_by: string | null;
  created_at: ISO;
  published_at: ISO | null;
}

export interface LintIssue {
  path: string;
  kind: "length" | "banned" | "fraud" | "duplicate" | string;
  message: string;
}

export interface ContentVersion {
  version: number;
  status: string;
  body: Row;
  lint: LintIssue[] | null;
  created_by: string;
  reviewed_by: string | null;
}

/** A story set check or lint finding (ADR 0026). check, banned and fraud block publishing. */
export interface StoryIssue {
  kind: "check" | "banned" | "fraud" | "duplicate" | "length" | string;
  path?: string;
  message: string;
}

export interface StoryStats {
  acts: number;
  chapters: number;
  beats: number;
  kinds: Record<string, number>;
  /** act key → summed money_pct of the act's beats */
  money_pct: Record<string, number>;
}

export interface StoryVersionRow {
  version: number;
  status: "draft" | "review" | "published" | "retired" | string;
  issues: number;
  created_by: string;
  reviewed_by: string | null;
  created_at: ISO;
  published_at: ISO | null;
}

export interface StoryVersion extends Omit<StoryVersionRow, "issues"> {
  body: Row;
  lint: StoryIssue[] | null;
  stats?: StoryStats;
}

// ---------------------------------------------------------------- courses and certificates (ADR 0023, SKL)

/** A course set check or lint finding. check, banned and fraud block publishing. */
export interface CourseIssue {
  kind: "check" | "banned" | "fraud" | "duplicate" | string;
  path?: string;
  message: string;
}

export interface CourseRow {
  course: string;
  title: string | null;
  published: number | null;
  latest: number;
  latest_status: "draft" | "review" | "published" | "retired" | string;
  certs: number;
  attempts: number;
  passed: number;
  held: number;
}

export interface CourseVersionRow {
  version: number;
  status: "draft" | "review" | "published" | "retired" | string;
  issues: number;
  created_by: string;
  reviewed_by: string | null;
  accuracy_by: string | null;
  created_at: ISO;
  published_at: ISO | null;
}

export interface CourseVersion extends Omit<CourseVersionRow, "issues"> {
  body: Row;
  lint: CourseIssue[] | null;
}

export interface CourseCheck {
  issues: CourseIssue[];
  /** False while any check, banned or fraud finding stands. */
  ok: boolean;
}

export interface CourseQuestionStat {
  /** Index into the published bank. */
  q: number;
  id?: string;
  text?: string;
  asked: number;
  right: number;
  rate: number;
  flag?: "too_hard" | "too_easy";
}

export interface CourseStats {
  market: string;
  course: string;
  learners: number;
  certs: number;
  attempts_by_status: { status: string; attempts: number }[];
  questions: CourseQuestionStat[];
}

/** A quiz pass held for being too fast (the quiz_fast signal). */
export interface HeldQuiz {
  id: string;
  account_id: string;
  name?: string;
  market: string;
  course: string;
  correct?: number;
  total?: number;
  started_at?: ISO;
  submitted_at?: ISO;
  seconds?: number | string;
}

export interface StoryCheck {
  version?: number;
  ok: boolean;
  issues: StoryIssue[] | null;
  stats?: StoryStats;
}

// ---------------------------------------------------------------- World Director (ADR 0027, WDR-09)
// The view shapes live with the page's pure logic (pages/director/logic.ts),
// which also normalises the loose JSON columns.

export type DirectorArc = Arc;
export type DirectorDirective = Directive;
export type DirectorDryDirective = DryDirective;
export type DirectorEnvelopeBody = EnvelopeBody;

export interface DirectorOverview {
  market: string;
  on: boolean;
  /** kind → paused, plus "all" (kill:director[:<MK>]). */
  paused: Record<string, boolean>;
  /** kind → {body, default, version?, updated_by?, approved_by?, ceilings: {limit: [min, max]}} (normalizeEnvelopes). */
  envelopes: Record<string, unknown>;
  /** The last 7 days by kind and status. */
  counts: { kind: string; status: string; n: number }[];
  arcs: Arc[];
  days: { day: ISO; applied: number | null; proposed: number | null }[];
  /** The latest hourly state the Director observed. */
  state: Row | null;
  kinds: string[];
}

/** A weekly economy report (APL-05): see director.WeeklyReportBody. */
export interface AutopilotReport {
  week: ISO;
  body: {
    market: string;
    week: string;
    mode: string;
    runs: Record<string, number>;
    days_in_range: number;
    days_measured: number;
    guards: string[];
    proposals: Record<string, Record<string, number>>;
    verdicts: { agree: number; disagree: number; pct: number; judged: number; unjudged: number };
    agreement_14d: { agree: number; disagree: number; pct: number; judged: number; unjudged: number };
    changes: { dial: string; kind: string; from: number; to: number; status: string; published_at: ISO; why: unknown; expected: unknown; evals: unknown }[];
    frozen: string[];
    dials: Record<string, number>;
    supply: number;
    liability?: Record<string, number>;
    summary: string[];
    start?: Record<string, unknown>;
    end?: Record<string, unknown>;
  };
  created_at: ISO;
}

export interface DirectorDryRun {
  days: number;
  directives: DryDirective[];
  /** kind → {applied?, proposed?} */
  summary: Record<string, { applied?: number; proposed?: number }>;
}

export interface DirectorHooks {
  holdout: boolean;
  /** day, items (Today cards), push ({kind, title, at} or null), holdout, created_at; see normalizeHook. */
  hooks: Row[];
}

// ---------------------------------------------------------------- Generated content (LW6, ADR 0027)

export interface GenSpend {
  reserved_micros: number;
  spent_micros: number;
  requests: number;
}

export interface GenOverview {
  market: string;
  /** gen:<MK>: players see generated (and template) content, and the job runs. */
  on: boolean;
  /** The market is live: the worker skips markets that aren't, whatever gen:<MK> says. */
  live?: boolean;
  /** kill:gen[:<MK>]: no model calls; templates fill every slot. */
  killed: boolean;
  /** kill:gen is set (every market). Cleared in the raw flags, with killswitch everywhere. */
  kill_global?: boolean;
  /** kill:gen:<MK> is set. */
  kill_market?: boolean;
  /** An API key is configured on the admin-api ("Draft with AI"). */
  key: boolean;
  /** The worker had a key on its last pass (batches); null before it has run. */
  worker_key?: boolean | null;
  worker_seen_at?: ISO | null;
  bandit: boolean;
  config: Record<string, unknown>;
  config_error?: string;
  levels: GenLevelRow[];
  counts: { kind: string; status: string; source: string; n: number }[] | null;
  batches: Row[] | null;
  spend: { batch: GenSpend; draft: GenSpend; budget_micros: number; draft_budget_micros: number };
  ready_rule: { days: number; max_overturn: number; min_reviewed: number };
}

export interface GenItem {
  id: string;
  kind: string;
  slot: string;
  city: string | null;
  district: string | null;
  status: string;
  source: "model" | "template" | "staff";
  model: string | null;
  prompt_version: string | null;
  output: unknown;
  checks: unknown;
  level: number;
  sampled: boolean;
  cost_micros: number;
  replaces: string | null;
  reviewed_by: string | null;
  reviewed_at: ISO | null;
  review_note: string | null;
  published_at: ISO | null;
  valid_from: ISO;
  valid_until: ISO;
  created_at: ISO;
}

export interface GenItemFull extends GenItem {
  subject: string | null;
  inputs: unknown;
  usage: unknown;
  attempts: number;
  batch_id: string | null;
  check_batch_id: string | null;
  requested_by: string | null;
  /** The exact system prompt of the item's prompt version. */
  prompt: string | null;
}

export interface HookBandit {
  market: string;
  on: boolean;
  holdout_pct: number;
  window_days: number;
  arms: string[];
  contexts: string[];
  stats: BanditStat[];
  /** context → arm → {alpha, beta}: what the bandit uses today. */
  posterior: Record<string, Record<string, { alpha: number; beta: number }>>;
}

export interface AIDraftRequest {
  beat: "scene" | "text";
  brief: string;
  city?: string;
  /** Character keys to use (none: all). */
  characters?: string[];
  /** The characters of the set being edited (it may not be published yet); none: the market's published set. */
  cast?: { key: string; name: string; role?: string }[];
}

export interface AIDraftResult {
  id: string;
  draft: { title: string; lines: { who: string; text: string; mood: string }[]; messages: string[]; who: string } | null;
  checks: { name: string; ok: boolean; detail?: string }[];
  ok: boolean;
  model: string;
  prompt_version: string;
  cost_micros: number;
}

export interface Segment {
  market: string;
  account_ids?: string[];
  active_since?: string;
  kind?: "" | "guest" | "full";
  payers?: boolean | null;
}

export interface SystemStatus {
  api: { ok: boolean; status?: number; ms?: number; error?: string };
  payments: { ok: boolean; status?: number; ms?: number; error?: string };
  database: { ok: boolean };
  redis: { ok: boolean };
  catalogue_versions: Record<string, number>;
  jobs: { job: string; run_key: string; status: string; detail: unknown; started_at: ISO; finished_at: ISO | null }[];
  failed_jobs: { job: string; run_key: string; detail: unknown; started_at: ISO }[];
  outbox_backlog: number;
  providers: Provider[] | null;
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  status: string;
  created_at: ISO;
  roles: Grants | null;
  last_seen: ISO | null;
}

// ---------------------------------------------------------------- world & social layer (spec v2 §10)

export type ModStatus = "hidden" | "visible" | "deleted";

export interface ChatMessage {
  id: string;
  channel: string;
  account_id: string;
  name: string;
  kind: string;
  body: string;
  status: ModStatus;
  reports: number;
  created_at: ISO;
  report_reasons: { reason: string; at: ISO }[] | null;
}

export interface GuestbookEntry {
  id: string;
  owner: string;
  owner_name: string;
  author: string;
  author_name: string;
  body: string;
  status: ModStatus;
  reports: number;
  created_at: ISO;
}

export interface Mute {
  account_id: string;
  name: string;
  scope: string;
  until: ISO | null;
  reason: string | null;
  by_staff: string | null;
  by_account: string | null;
  created_at: ISO;
}

export interface Word {
  market: string;
  word: string;
  created_by: string;
  created_at: ISO;
}

export interface AudioRoom {
  id: string;
  kind: string;
  ref: string;
  title: string;
  status: string;
  host_id: string | null;
  host: string | null;
  created_at: ISO;
  ended_at: ISO | null;
  ended_by: string | null;
  in_room: number;
  connected: number;
  on_stage: number;
  reports: number;
  listen_seconds: number;
}

export interface RoomMember {
  account_id: string;
  name: string;
  role: string;
  connected: boolean;
  hand_raised_at: ISO | null;
  joined_at: ISO;
  left_at: ISO | null;
  listen_seconds: number;
}

export interface RoomDetail {
  /** The audio.rooms row. */
  room: Row;
  members: RoomMember[] | null;
  reports: { id: string; reporter: string; target_kind: string; target_id: string; reason: string; status: string; created_at: ISO }[] | null;
}

export interface DJ {
  district: string;
  account_id: string;
  name: string;
  added_by: string;
  created_at: ISO;
}

export interface EventKind {
  key: string;
  name: string;
  fx: Record<string, number>;
  audio: boolean;
}

export interface WorldOverview {
  cities: { key: string; name: string; state: string; kind: string; home: boolean; unlock_level: number; districts: number; landmarks: number; players_located: number; arrivals_24h: number }[];
  top_landmarks: { landmark: string; checkins: number }[] | null;
  stats: Record<string, number>[] | null;
  event_kinds: EventKind[] | null;
}

export interface WorldEvent {
  id: string;
  market: string;
  kind: string;
  title: string;
  descr: string;
  district: string | null;
  starts_at: ISO;
  ends_at: ISO;
  fx: Record<string, number> | null;
  audio: boolean;
  sponsor_campaign: string | null;
  status: string;
  created_by: string;
  created_at: ISO;
  rsvps: number;
  live_room: string | null;
}

export interface Poll {
  id: string;
  market: string;
  district: string | null;
  question: string;
  options: string[];
  starts_at: ISO;
  ends_at: ISO;
  sponsor_campaign: string | null;
  status: string;
  created_by: string;
  /** option index (as a string) → votes */
  results: Record<string, number> | null;
}

export interface ShowcaseEntry {
  id: string;
  showcase: string;
  day: ISO;
  account_id: string;
  name: string;
  item_name: string;
  votes: number;
  status: string;
}

export const FX_KEYS = ["clout", "haggle", "showcase", "rent", "ride", "biz"] as const;

/** Event FX that boost free play (ADR 0018). They count toward the 40% ceiling on free income. */
export const FREE_FX_KEYS = ["pocket", "mission", "quest", "ad", "job"] as const;
export const FX_LABELS: Record<string, string> = { pocket: "Pocket money", mission: "Missions", quest: "Quests", ad: "Ads", job: "Jobs" };

export interface Placement {
  key: string;
  name: string;
  format: "banner" | "card" | "title" | "poster" | "takeover" | "item" | "video" | string;
  where: string;
}

export interface InventoryRow {
  placement: string;
  live_campaigns: number;
  impressions_24h: number;
  clicks_24h: number;
}

/** Why a requested placement showed no ad (the furthest any candidate campaign got). */
export type UnfilledReason = "no_campaign" | "targeting" | "caps" | "pacing" | "budget" | "separation";
export type Unfilled = Record<UnfilledReason, number>;

/** Fill: placements that showed an ad ÷ placements requested (null without requests). */
export interface AdFill {
  requested: number;
  filled: number;
  fill: number | null;
  unfilled: Unfilled;
}

/** Fill for one placement family over the last 24 hourly buckets (from GET /admin/v1/ads/inventory). */
export interface AdFillPlacement {
  placement: string;
  requested_24h: number;
  filled_24h: number;
  fill_24h: number | null;
  unfilled_24h: Unfilled;
}

export interface CreativePayload {
  title: string;
  body?: string;
  cta?: string;
  url?: string;
  image_url?: string;
  /** branded_item:<store>: the catalogue item it is sold as (checked against the market). */
  item?: string;
  /** branded_item: display name. */
  name?: string;
  /** rewarded_video: https video; min_seconds 5–30. */
  video_url?: string;
  min_seconds?: number;
  /** sponsored_mission / sponsored_quest / crew_challenge: the task to present (normal reward). */
  mission?: string;
  quest?: string;
  challenge?: string;
  /** emote_pack: the pack it is sold as. */
  pack?: string;
  /** district_takeover: brand colour #RRGGBB. */
  accent?: string;
}

export interface Creative {
  id: string;
  campaign_id: string;
  brand: string;
  market: string;
  placement: string;
  payload: CreativePayload;
  status: "pending" | "approved" | "rejected" | string;
  review_note: string | null;
  reviewed_by: string | null;
  created_by: string;
  created_at: ISO;
  /** Bumped on every edit; creative.review must name the version reviewed. */
  version: number;
}

export interface Targeting {
  cities?: string[];
  min_level?: number;
  max_level?: number;
  kinds?: string[];
  payers?: boolean;
  in_crew?: boolean;
  platforms?: string[];
}

export interface CampaignConfig {
  campaign_id: string;
  placements: string[];
  targeting: Targeting;
  priority: number;
  daily_cap: number | null;
  freq_cap: number | null;
  pricing: Pricing;
  price_minor: number;
  budget_minor: number | null;
}

export type Pricing = "flat" | "flat_day" | "cpm" | "cpc";
export type CampaignStatus = "draft" | "submitted" | "rejected" | "scheduled" | "live" | "paused" | "ended" | "cancelled";

/** A campaign row from GET /campaigns (v1 columns, the v2 settings and delivery, the v3 partner side). */
export type Campaign = Row & {
  id: string;
  brand: string;
  category: string;
  market: string;
  placement: string;
  placements: string[] | null;
  targeting: Targeting | null;
  priority: number | null;
  daily_cap: number | null;
  freq_cap: number | null;
  pricing: Pricing | null;
  /** In the market's currency. */
  price_minor: number | null;
  /** In the billing currency. */
  budget_minor: number | null;
  fee_minor: number;
  currency: string;
  status: CampaignStatus | string;
  starts_at: ISO;
  ends_at: ISO;
  age_restricted: boolean;
  impressions: number;
  taps: number;
  buy_clicks: number;
  creatives_approved: number;
  creatives_pending: number;
  name: string | null;
  partner_id: string | null;
  billing_currency: string | null;
  fx_rate: string | null;
  submitted_at: ISO | null;
  approved_by: string | null;
  rejection_note: string | null;
  /** Who paused it: staff resume only their own pauses. */
  paused_by: "partner" | "staff" | "suspension" | "system" | null;
  configured_by: string | null;
  /** Everyone who ran campaign.configure since submission: none of them may review it. */
  configurers: string[] | null;
  created_at: ISO;
  /** Media cost so far in the billing currency (capped at the budget). */
  spend_minor: number | null;
};

/** One cell of a report breakdown (staff reports carry raw numbers). */
export interface ReportRow {
  key: string;
  local?: string;
  impressions: number | string;
  clicks: number | string;
  reach?: number | string | null;
  ctr?: number | null;
  spend_minor?: number | string;
}

export interface CampaignReport {
  campaign_id: string;
  name?: string;
  brand: string;
  market: string;
  status: string;
  pricing: Pricing | string;
  /** Billing currency. */
  currency: string;
  /** Market currency (price_minor). */
  price_currency?: string;
  price_minor?: number;
  fx_rate?: string;
  partner_id?: string | null;
  timezone?: string;
  starts_at: ISO;
  ends_at: ISO;
  impressions: number;
  clicks: number;
  ctr: number;
  reach?: number | null;
  frequency?: number | null;
  spend_minor?: number;
  ecpm_minor?: number;
  ecpc_minor?: number;
  cost_minor: number;
  budget_minor: number | null;
  budget_used?: number;
  pacing?: { daily_cap?: number; today: number; plan_share: number; delivered_share: number };
  daily: { day: string; placement: string; impressions: number; clicks: number; cost_minor: number; currency: string }[];
  days?: ReportRow[];
  hourly?: ReportRow[];
  by_placement?: ReportRow[];
  by_city?: ReportRow[];
  by_platform?: ReportRow[];
  /** Brand employers and course sponsors only (BRD-06): what the workforce did. Exact for staff. */
  workforce?: { kind: "application" | "hire" | "shift" | "beat" | "course" | string; events: number; workers: number }[];
}

// ---------------------------------------------------------------- ads v3: partners, billing, rate cards (ADR 0017)

export type PartnerStatus = "draft" | "pending" | "verified" | "rejected" | "suspended";

export interface PartnerRow {
  id: string;
  display_name: string;
  legal_name: string;
  kind: string;
  country: string;
  billing_currency: string;
  category: string;
  website: string;
  status: PartnerStatus;
  submitted_at: ISO | null;
  reviewed_by: string | null;
  reviewed_at: ISO | null;
  created_at: ISO;
  campaigns: number;
}

export type PartnerProfile = PartnerRow & {
  registration_no: string | null;
  tax_id: string | null;
  contact_phone: string;
  address: string;
  verified_domains: string[] | null;
  verified_website: string | null;
  review_note: string | null;
  brand_id: string | null;
  brand_name: string | null;
  /** Bumped on every profile edit; partner.verify must name the version checked. */
  profile_version: number;
  updated_at: ISO;
};

export interface PartnerUser {
  id: string;
  email: string;
  name: string;
  role: "owner" | "manager" | "analyst";
  status: string;
  created_at: ISO;
  last_login_at: ISO | null;
}

export interface PartnerCampaignRow {
  id: string;
  name: string;
  market: string;
  status: string;
  pricing: string;
  budget_minor: number | null;
  billing_currency: string | null;
  starts_at: ISO;
  ends_at: ISO;
  submitted_at: ISO | null;
}

export interface PartnerBalance {
  available_minor: number;
  held_minor: number;
  balance_minor: number;
  currency: string;
}

export interface PartnerDetail {
  partner: PartnerProfile;
  users: PartnerUser[] | null;
  campaigns: PartnerCampaignRow[] | null;
  balance?: PartnerBalance;
  /** Registration and tax numbers, phone, address and team emails are masked (sponsorship.read without partners.review or ads.billing). */
  pii_masked?: boolean;
}

export interface PartnerLedgerEntry {
  id: string;
  kind: "topup" | "hold" | "spend" | "release" | "adjustment" | "refund" | "chargeback";
  /** Change to available. */
  amount_minor: number;
  /** Change to held. */
  held_minor: number;
  currency: string;
  campaign_id: string | null;
  order_id: string | null;
  memo: string;
  created_by: string;
  created_at: ISO;
}

export interface PartnerInvoice {
  id: string;
  number: string | null;
  kind: string;
  period: string | null;
  amount_minor: number;
  net_minor: number | null;
  tax_minor: number;
  tax_rate: string;
  currency: string;
  status: string;
  order_id: string | null;
  method: string | null;
  paid_at: ISO | null;
  created_at: ISO;
}

export interface PartnerBilling {
  balance: PartnerBalance;
  ledger: PartnerLedgerEntry[] | null;
  invoices: PartnerInvoice[] | null;
}

export interface BalanceRow {
  id: string;
  display_name: string;
  country: string;
  currency: string;
  status: PartnerStatus;
  available_minor: number;
  held_minor: number;
  balance_minor: number;
  topups_minor: number;
  spend_minor: number;
  last_entry_at: ISO | null;
}

export interface RateCard {
  market: string;
  placement: string;
  pricing: "cpm" | "cpc" | "flat_day";
  price_minor: number;
  min_budget_minor: number;
  updated_by: string;
  updated_at: ISO;
}

export interface AdsOverview {
  days: number;
  since: ISO;
  markets: { market: string; live_campaigns: number; impressions: number; clicks: number; reach_daily_sum: number }[] | null;
  daily: { day: string; impressions: number; clicks: number }[] | null;
  top_campaigns:
    | { id: string; name: string; brand: string | null; market: string; status: string; partner_id: string | null; pricing: string; impressions: number; clicks: number; reach: number | null }[]
    | null;
  /** Present with ads.billing or sponsorship.write. */
  revenue?: { market: string; currency: string; spend_minor: number }[] | null;
  /** Fill per market in the window (as of the last ads_rollup, every 15 minutes). */
  fill: (AdFill & { market: string })[] | null;
  fill_total: AdFill;
}

export interface AdAsset {
  asset_key: string;
  url: string;
  format: string;
  content_type: string;
  width: number;
  height: number;
  bytes: number;
}

export type WaitlistKind = "player" | "brand" | "creator" | "press";
export type WaitlistStatus = "new" | "contacted" | "closed" | "spam";

export interface WaitlistEntry {
  id: string;
  kind: WaitlistKind;
  /** Masked (a•••@d•••.com) unless the viewer holds players.pii. */
  email: string;
  name: string;
  company: string;
  market: string | null;
  message: string;
  source: string;
  status: WaitlistStatus;
  created_at: ISO;
}

export interface OnboardingFunnel {
  days: number;
  cohort: { accounts: number; intro_seen: number; made_look: number; settled: number }[] | null;
  /** median_hours: from sign-up to the step being done. */
  steps: { step: string; done: number; claimed: number; median_hours: number | null }[] | null;
  interests: { interest: string; count: number }[] | null;
}

export interface Growth {
  signups: { provider: string; d1: number; d7: number; d30: number }[] | null;
  social: Record<string, number>[] | null;
  crew_joins_by_route: { via: string; count: number }[] | null;
}

export interface Integrations {
  auth: Record<string, boolean>;
  livekit: { configured: boolean; url?: string };
}

/** Jobs admin-api will queue for a re-run (mirrors `rerunnable` in internal/admin/modules.go). */
export const RERUNNABLE_JOBS = [
  "close_cups",
  "close_ajo",
  "close_parties",
  "expire_rounds",
  "compute_index",
  "ensure_pulse",
  "wallet_integrity",
  "liability_snapshot",
  "spend_reviews",
  "device_graph",
  "audit_anchor",
] as const;

// ---------------------------------------------------------------- device graph (WAL-09) and audit anchors

export type ClusterReview = "open" | "flagged" | "cleared";
export type HoldReason = "device_seen" | "ip_velocity" | "net_velocity" | "flagged_cluster" | string;
export type HoldStatus = "held" | "released" | "denied";

export interface DeviceCluster {
  /** The earliest account in the cluster (stable id for reviews). */
  root: string;
  root_name?: string | null;
  market: string;
  size: number;
  gifts_paid: number;
  gifts_held: number;
  paying: number;
  /** How many device fingerprints / (address, day) pairs are shared inside the cluster. */
  signals: { device?: number; address_days?: number } | null;
  first_at: ISO;
  last_at: ISO;
  computed_at: ISO;
  review: ClusterReview;
  note: string;
  reviewed_by: string | null;
  reviewed_at: ISO | null;
}

export interface ClusterMember {
  account_id: string;
  name: string;
  market: string;
  kind: string;
  status: string;
  created_at: ISO;
  devices: number;
  gift_paid: boolean;
  hold_status: HoldStatus | null;
  hold_reasons: HoldReason[] | null;
  phone_verified: boolean;
  paying: boolean;
  balance: number;
  /** 8-character prefixes of device fingerprint hashes. */
  device_tags: string[] | null;
  /** 8-character prefixes of sign-up address hashes. */
  address_tags: string[] | null;
}

export interface WelcomeHold {
  account_id: string;
  name: string;
  market: string;
  amount: number;
  reasons: HoldReason[] | null;
  status: HoldStatus;
  resolved_by: string | null;
  created_at: ISO;
  resolved_at: ISO | null;
  cluster: string | null;
}

export interface AuditVerifyResult {
  ok: boolean;
  checked: number;
  head?: string;
  head_seq?: number;
  broken_at_seq?: number;
  empty?: boolean;
}

export interface AuditAnchorReport {
  ok: boolean;
  store: string;
  configured: boolean;
  anchors: number;
  latest?: { v: number; env: string; seq: number; hash: string; rows: number; at: ISO; prev: string; digest: string } | null;
  chain: AuditVerifyResult;
  problems: { at: string; seq: number; detail: string }[] | null;
}

// ---------------------------------------------------------------- UGC moderation (ADR 0024)

export type UGCQueueStatus = "open" | "approved" | "rejected" | "removed" | "dismissed";
export type UGCKind = "crew_name" | "crew" | "player" | "business" | "design" | "appeal" | (string & {});
/** Creator trust tier (internal/moderation/trust.go). */
export type CreatorTier = "new" | "standard" | "trusted" | "partner" | (string & {});

/** One entry in ops.mod_queue. version > 0: a submitted version (approve makes it live); 0: the item itself or reports on it. */
export interface UGCQueueItem {
  id: string;
  market: string;
  item_kind: UGCKind;
  item_id: string;
  version: number;
  owner: string | null;
  owner_name: string | null;
  owner_tier: CreatorTier | null;
  /** 0 urgent (minor safety, sexual, threats, age probing), 1 high (scams, hate), 2 normal, 3 low. */
  priority: number;
  /** e.g. "report:scam", "rule:job_scam", "name:<term>", "name_review", "appeal", a filter kind. */
  reasons: string[] | null;
  /** What was submitted, by kind (business: name, slogan, about, logo…; design: name, kind, look, spec, price, creator?; crew_name: name, matched; appeal: strike, reason, text). */
  snapshot: Record<string, unknown> | null;
  reports: number;
  status: UGCQueueStatus;
  decided_by: string | null;
  decided_at: ISO | null;
  note: string | null;
  created_at: ISO;
}

export interface UGCStrike {
  id: string;
  account_id: string;
  name: string;
  market: string;
  item_kind: string | null;
  item_id: string | null;
  reason: string;
  severe: boolean;
  /** moderation.Consequence; mute is a Go duration in nanoseconds. */
  consequence: { warning?: boolean; mute?: number; mute_hours?: number; hide_creations?: boolean; remove_item?: boolean; propose_suspend?: boolean } | null;
  issued_by: string;
  created_at: ISO;
  expires_at: ISO;
  voided_at: ISO | null;
  voided_by: string | null;
  active: boolean;
}

/** Designed goods (DSN, ADR 0024). */
export type DesignKind = "outfit" | "decor" | "menu";
export type DesignStatus = "draft" | "pending" | "live" | "hidden" | "removed" | "rejected" | "retired";

/** What a design looks like, with resolved hex colours (openapi DesignLook): draw it without the palette. */
export interface DesignLook {
  kind: DesignKind | (string & {});
  design?: string;
  name?: string;
  // outfit (base is an avatar outfit key) and decor (base is a decor item key)
  base?: string;
  cloth?: string;
  trim?: string;
  accent?: string;
  pattern?: string;
  accessory?: string;
  // decor
  base_kind?: string;
  shape?: string;
  material?: string;
  primary?: string;
  secondary?: string;
  motif?: string;
  // menu
  dish?: string;
  offering?: string;
  plating?: string;
  sauce?: string;
  garnishes?: string[] | null;
}

/** One row of GET /admin/v1/ugc/designs. Amounts are play money. */
export interface UGCDesign {
  id: string;
  /** Share code (/g/<code>). */
  code: string;
  market: string;
  kind: DesignKind;
  name: string;
  base: string;
  /** Play money: what a buyer pays (outfits, decor), or the designer's royalty per order (menu items). */
  price: number;
  look: DesignLook | null;
  status: DesignStatus;
  /** Why it's hidden: by reports, or by a strike on the designer. */
  hidden_by: "reports" | "strike" | (string & {}) | null;
  /** A name change waiting for a moderator. */
  pending_name: string | null;
  creator_acc: string;
  creator_name: string;
  sales: number;
  /** Play money, before production cost and sales tax. */
  gross: number;
  favourites: number;
  rating_n: number;
  /** Bayesian average, 0 with no ratings. */
  rating_bayes: number;
  trend_rank: number | null;
  staff_pick: boolean;
  /** A paid "Featured" slot, while it lasts. */
  featured_until: ISO | null;
  /** Open reports. */
  reports: number;
  created_at: ISO;
  published_at: ISO | null;
}

export type AppealStatus = "open" | "upheld" | "overturned";
export interface UGCAppeal {
  id: string;
  account_id: string;
  name: string;
  ref_kind: string;
  ref_id: string;
  text: string;
  status: AppealStatus;
  decided_by: string | null;
  decided_at: ISO | null;
  note: string | null;
  created_at: ISO;
  strike_reason: string | null;
  severe: boolean | null;
  issued_by: string | null;
  market: string | null;
}

export type ReservedKind = "trademark" | "public_figure" | "government" | "platform" | "slur";
export type ReservedAction = "blocked" | "review" | "partner_only";
export interface ReservedName {
  id: string;
  /** A market code, or "*" for every market. */
  market: string;
  term: string;
  folded: string;
  kind: ReservedKind;
  action: ReservedAction;
  partner_id: string | null;
  note: string | null;
  source: string;
  removed: boolean;
  created_by: string | null;
  created_at: ISO;
}

// ---------------------------------------------------------------- the optional classifier (UGM-11)

export interface LLMLabelMetrics {
  support: number;
  tp: number;
  fp: number;
  fn: number;
  /** null: nothing predicted. */
  precision: number | null;
  /** null: no cases with the label. */
  recall: number | null;
}
export interface LLMMetrics {
  cases: number;
  scored: number;
  errors: number;
  flag: LLMLabelMetrics;
  labels: Record<string, LLMLabelMetrics>;
  /** Case ids only (seed:<id> or queue:<queue id>), never text. */
  misses: { case_id: string; expected: string[] | null; got: string[] | null; severity: string; error?: string }[] | null;
  cost_micros: number;
  stopped?: string;
}
export interface LLMEval {
  id: string;
  market: string;
  model: string;
  prompt_version: string;
  seed_cases: number;
  queue_cases: number;
  metrics: LLMMetrics;
  baseline: LLMMetrics | null;
  passed_then: boolean;
  cost_micros: number;
  started_by: string;
  created_at: ISO;
}
export interface LLMGate {
  market: string;
  platform: boolean;
  flag: boolean;
  /** The latest evaluation meets today's thresholds. */
  passes: boolean;
  runs: boolean;
  why: string[] | null;
  eval: LLMEval | null;
}
export interface LLMPR {
  precision: number;
  recall: number;
}
export interface LLMConfig {
  model: string;
  effort: string;
  timeout_s: number;
  max_items_per_run: number;
  lookback_hours: number;
  daily_budget_usd: number;
  eval_budget_usd: number;
  eval_max_cases: number;
  /** Decided queue items (real player text) join the seed cases. */
  eval_from_queue: boolean;
  price_in_per_mtok: number;
  price_out_per_mtok: number;
  queue_severity: string;
  hide_severity: string;
  hide_confidence: number;
  thresholds: { min_cases: number; max_error_rate: number; flag: LLMPR; labels: Record<string, LLMPR> | null; min_label_support: number };
}
export interface UGCClassifier {
  config: LLMConfig;
  config_error: string;
  defaults: LLMConfig;
  prompt_version: string;
  labels: string[];
  spent_today_usd: number;
  markets: LLMGate[] | null;
  activity: { market: string; classified: number; queued: number; hidden: number; raised: number; unclassifiable: number; cost_micros: number }[] | null;
  evals:
    | {
        id: string;
        market: string;
        model: string;
        prompt_version: string;
        seed_cases: number;
        queue_cases: number;
        passed: boolean;
        why: string[] | null;
        cost_micros: number;
        started_by: string;
        created_at: ISO;
        flag: LLMLabelMetrics | null;
        stopped: string | null;
      }[]
    | null;
  runs: { job: string; run_key: string; status: string; detail: string | null; started_at: ISO }[] | null;
}

// ---------------------------------------------------------------- player businesses (ADR 0021, 0024)

export type BizOpStatus = "open" | "arrears" | "bankrupt" | "closed" | "frozen";
export type BizModStatus = "draft" | "pending" | "live" | "hidden" | "removed" | "rejected";

/** Money columns are play money; numeric columns may arrive as strings, so pages Number() them. */
export interface Business {
  id: string;
  code: string;
  kind: string;
  name: string;
  district: string;
  owner_acc: string;
  owner_name: string | null;
  mod_status: BizModStatus;
  op_status: BizOpStatus;
  version: number;
  treasury: number | null;
  deposit: number;
  injected: number;
  drawn: number;
  arrears: number;
  arrears_since: ISO | null;
  rent_day: number;
  condition: number;
  quality: number;
  reputation: number | string;
  created_at: ISO;
  closed_at: ISO | null;
  npc_7d: number | string;
  player_7d: number | string;
}

/** One local day of a market's NPC demand pool. */
export interface DemandDay {
  day: ISO;
  dau: number;
  dau7: number | string;
  pool: number;
  mult: number | string;
  spent: number;
}

export interface BizView {
  market: string;
  /** The ugc:biz:<MK> flag. */
  enabled: boolean;
  businesses: Business[];
  demand: DemandDay[];
  /** The econ:demand:<MK> flag: an override that scales the pool until `until`. */
  override: { mult: number; until: ISO } | null;
}

// ---------------------------------------------------------------- business and P2P dials (ECN-14a)

/** economy.biz. Percentages are % of Pack 2; the int fields must stay integers (admin-api decodes strictly). */
export interface BizCfg {
  npc_demand_pct: number;
  hour_curve: number[];
  category_weights: Record<string, number>;
  price_elasticity: number;
  arrears_days: number;
  arrears_demand_mult: number;
  condition_decay: number;
  repair_pct_per_point: number;
  inject_day_pct: number;
  exit_fee_days: number;
  ref_dau_per_business: number;
  peak_share_mult: number;
  min_account_days: number;
  /** By creator trust tier: new, standard, trusted, partner. */
  max_per_owner: Record<string, number>;
}

/** economy.tax. Rates are fractions (0.10 = 10%), except listing_fee_pct (% of Pack 2). */
export interface TaxCfg {
  sales_tax: number;
  turnover_tax: number;
  payroll_tax: number;
  dividend_tax: number;
  stamp_duty: number;
  listing_fee_pct: number;
  cogs: number;
  production: number;
  min_hop_friction: number;
  civic_pct: number;
}

export interface ReceiveTier {
  min_days: number;
  phone: boolean;
  day_pct: number;
}

/** economy.p2p. Caps are % of Pack 2. */
export interface P2PCfg {
  full_only: boolean;
  min_account_days_receive: number;
  min_transfer_pct: number;
  receive_tiers: ReceiveTier[];
  week_mult: number;
  pair_day_pct: number;
  pair_day_pct_shop: number;
  unpaid_send_day_pct: number;
  clearing_hours: number;
  clearing_hours_recent_fund: number;
  clearing_hours_flagged: number;
  recent_fund_hours: number;
  new_payer_days: number;
  work_shifts_per_day: number;
}

/** world.ugc.premises: one row per district kind. */
export interface PremisesCfg {
  district_kind: string;
  units: number;
  rent_pct: number;
  deposit_days: number;
  tier: string;
}

export interface BizEconPreview {
  market: string;
  pack2_play: number;
  dau7: number;
  businesses: number;
  npc_pool_day: number;
  npc_mint_day: number;
  npc_pool_per_business: number;
  npc_mint_per_dau_pct: number;
  /** The least a P2P hop loses, in percent. */
  min_hop_loss_pct: number;
  categories: { category: string; weight: number; share: number }[];
  payback: { kind: string; name: string; district_kind: string; setup: number; rent_day: number; base_days?: number; peak_days?: number; ok: boolean }[];
  invariants: string[];
  sim?: FreePreview["sim"];
  sim_error?: string;
}

export interface BizEconomy {
  market: string;
  version: number;
  biz: BizCfg;
  tax: TaxCfg;
  p2p: P2PCfg;
  /** null when the market has no businesses block (then premises can't be sent). */
  premises: PremisesCfg[] | null;
  ceilings: { npc_demand_pct: number; wage_per_shift_pct: number; receive_daily_pct: number; min_hop_friction: number };
  preview: BizEconPreview;
}

/** Whole blocks only: admin-api rejects unknown fields. */
export interface BizEconProposal {
  market: string;
  biz?: BizCfg;
  tax?: TaxCfg;
  p2p?: P2PCfg;
  premises?: PremisesCfg[];
  sim?: boolean;
  notes?: string;
}

export interface BizEconDrafted {
  version: number;
  invariants: string[] | null;
  sim?: FreePreview["sim"];
  sim_error?: string;
  preview: BizEconPreview;
}

const bizEconPv = (p: BizEconPreview): BizEconPreview => ({ ...p, categories: p.categories ?? [], payback: p.payback ?? [], invariants: p.invariants ?? [] });

// ---------------------------------------------------------------- music studio (ADR 0100)

/** Fetches a file with the bearer token as a Blob, for an <audio> element (which can't send the Authorization header). */
export async function fetchBlob(path: string, query?: Query): Promise<Blob> {
  const res = await raw("GET", path, undefined, query);
  return res.blob();
}

export type MusicKind = "song" | "instrumental" | "bed" | "stinger" | "loop" | "jingle";
export type MusicStatus = "draft" | "published" | "retired";

export interface MusicOverview {
  engines: string[];
  default_engine: string;
  mastering: string;
  storage: boolean;
  songwriter: boolean;
  config: { daily_budget_usd: number };
  counts: { market: string; status: MusicStatus; n: number }[];
  spend_today: { market: string; pool: string; micros: number; requests: number }[];
  kinds: MusicKind[];
  prompt_version: string;
  max_candidates: number;
}

export interface MusicBrief {
  kind: MusicKind;
  title?: string;
  genre: string;
  mood?: string;
  theme?: string;
  instruments?: string;
  tags?: string[];
  bpm?: number;
  key?: string;
  language?: string;
  duration_s: number;
  lyrics?: string;
  auto_lyrics?: boolean;
  instrumental?: boolean;
  candidates?: number;
  engine?: string;
  seed?: number;
  notes?: string;
}

export interface MusicTrackRow {
  id: string;
  market: string;
  title: string;
  kind: MusicKind;
  status: MusicStatus;
  tags: string[] | null;
  bpm: number | null;
  key: string | null;
  duration_ms: number | null;
  current_version: string | null;
  created_by: string;
  approved_by: string | null;
  created_at: ISO;
  published_at: ISO | null;
  score: number | null;
  engine: string | null;
  peaks: number[] | null;
  licence: string | null;
  versions: number;
  job_status: string | null;
}

export interface MusicVersion {
  id: string;
  n: number;
  batch: string;
  status: string;
  engine: string;
  model: string;
  licence: string;
  prompt: string;
  lyrics: string;
  seed: number | null;
  params: Record<string, unknown>;
  content_type: string;
  alt_content_type: string;
  has_alt: boolean;
  bytes: number;
  duration_ms: number;
  loudness_lufs: number | null;
  true_peak_db: number | null;
  peaks: number[];
  metrics: Record<string, number | string>;
  score: number | null;
  score_detail: Record<string, number>;
  cost_micros: number;
  created_by: string;
  created_at: ISO;
}

export interface MusicJob {
  id: string;
  kind: "generate" | "cut";
  status: "queued" | "running" | "done" | "failed";
  progress: number;
  step: string;
  attempts: number;
  run_after: ISO;
  error: string;
  result: Record<string, unknown>;
  created_by: string;
  created_at: ISO;
  finished_at: ISO | null;
}

export interface MusicTrackDetail {
  track: Omit<MusicTrackRow, "score" | "engine" | "peaks" | "licence" | "versions" | "job_status"> & { brief: MusicBrief; lyrics: string; updated_at: ISO };
  versions: MusicVersion[];
  jobs: MusicJob[];
}

export interface MusicPlaylist {
  id: string;
  market: string;
  name: string;
  kind: "mix" | "radio_bed" | "venue";
  created_by: string;
  updated_at: ISO;
  items: { track_id: string; title: string; status: MusicStatus; duration_ms: number | null }[];
}

export interface MusicPlacement {
  id: string;
  market: string;
  target_kind: string;
  target: string;
  track_id: string | null;
  track_title: string | null;
  playlist_id: string | null;
  playlist_name: string | null;
  radius_m: number;
  volume: number;
  schedule: { from?: number; to?: number };
  enabled: boolean;
  created_by: string;
  approved_by: string | null;
  updated_at: ISO;
}

export interface MusicCue {
  id: string;
  market: string;
  key: string;
  track_id: string;
  track_title: string;
  track_status: MusicStatus;
  volume: number;
  duck: boolean;
  enabled: boolean;
  created_by: string;
  approved_by: string | null;
  updated_at: ISO;
}

export interface MusicTargets {
  market: string;
  districts: { key: string; name: string; sub?: string }[];
  landmarks: { key: string; name: string; sub?: string }[];
  interiors: { key: string; name: string; sub?: string }[];
  vehicles: string[];
  cue_keys: string[];
  target_kinds: string[];
  playlist_kinds: string[];
}

// ---------------------------------------------------------------- world radio (ADR 0100)

export interface RadioStation {
  market: string;
  name: string;
  enabled: boolean;
  persona_id: string | null;
  bed_playlist_id: string | null;
  veto_minutes: number;
  horizon_minutes: number;
  dj_every: number;
  news_every_minutes: number;
  id_every_minutes: number;
  min_separation: number;
}

export interface RadioPersona {
  id: string;
  market: string;
  name: string;
  bio: string;
  voice_provider: string;
  voice_id: string;
  language: string;
  style: string;
  speaking_rate: number;
  active: boolean;
  created_by: string;
  updated_at: ISO;
}

export interface RadioOverview {
  market: string;
  station: RadioStation;
  on_air: boolean;
  feature_on: boolean;
  killed: boolean;
  personas: RadioPersona[];
  bed_playlists: { id: string; name: string }[];
  spend_today: { pool: string; micros: number; requests: number }[];
  budget_usd: number;
  playable_tracks: number;
  scheduled_items: number;
  voice_providers: string[];
  all_providers: string[];
  writer: boolean;
  writer_model: string;
  prompts: { bulletin: string; link: string };
}

export interface RadioQueueItem {
  id: string;
  kind: "track" | "dj" | "news" | "id";
  status: "scheduled" | "skipped" | "vetoed";
  starts_at: ISO;
  ends_at: ISO;
  track_id: string | null;
  title: string;
  segment_id: string | null;
  script: string | null;
  source: "model" | "template" | "staff" | null;
  model: string | null;
  prompt_version: string | null;
  veto_until: ISO | null;
  created_by: string | null;
  cost_micros: number | null;
  dj: string | null;
  segment_status: string | null;
}

export interface RadioSegment {
  id: string;
  kind: "dj" | "news" | "id";
  status: "ready" | "vetoed";
  source: "model" | "template" | "staff";
  model: string;
  prompt_version: string;
  script: string;
  facts: { kind: string; city?: string; place?: string; text: string; starts?: string; until?: string }[];
  duration_ms: number;
  veto_until: ISO;
  created_by: string;
  created_at: ISO;
  cost_micros: number;
  vetoed_by: string | null;
  vetoed_at: ISO | null;
  vetoed_reason: string | null;
  dj: string | null;
  scheduled: boolean;
  airs_at: ISO | null;
}

// ---------------------------------------------------------------- endpoints

// ---------------------------------------------------------------- platform event log (ADR 0095)

export type LogKind = "http.in" | "http.out" | "ws" | "app" | "job" | "log" | "platform";

export interface LogEvent {
  id: number;
  at: string;
  service: string;
  instance?: string;
  kind: LogKind;
  /** 10 debug, 20 info, 30 warn, 40 error. */
  level: number;
  name: string;
  method?: string;
  route?: string;
  path?: string;
  status?: number;
  dur_ms?: number;
  bytes_in?: number;
  bytes_out?: number;
  request_id?: string;
  account_id?: string;
  market?: string;
  actor?: string;
  ip_hash?: string;
  target?: string;
  err_code?: string;
  message?: string;
  fields?: Record<string, unknown>;
  /** How many real events this row stands for (1 unless an ordinary success was sampled). */
  weight?: number;
}

export interface LogsQuery {
  from?: string;
  to?: string;
  service?: string;
  kind?: string;
  level?: string;
  status?: string;
  route?: string;
  target?: string;
  account?: string;
  request_id?: string;
  market?: string;
  actor?: string;
  err_code?: string;
  q?: string;
  min_ms?: number;
  limit?: number;
  before?: string;
  after_id?: number;
}

export interface LogsPage {
  rows: LogEvent[];
  next?: string;
  now: string;
}

export interface LogsConfig {
  enabled: boolean;
  retention_days: number;
  sample_in: number;
  sample_4xx: number;
  sample_out: number;
  slow_ms: number;
  max_per_second: number;
  off?: string[];
  ignore?: string[];
}

export interface LogsSettings {
  config: LogsConfig;
  defaults: LogsConfig;
  saved: boolean;
  updated_by?: string;
  updated_at?: string;
  limits: Record<string, number>;
  kinds: string[];
}

export interface LogsTotals {
  requests: number;
  err_5xx: number;
  err_4xx: number;
  slow: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  max_ms: number;
  outbound: number;
  outbound_failed: number;
  app_events: number;
  warnings: number;
  errors: number;
  jobs_failed: number;
  rows: number;
}

export interface LogsStats {
  window: string;
  from: string;
  to: string;
  totals: LogsTotals;
  series: { t: string; requests: number; err_5xx: number; err_4xx: number; outbound: number; problems: number }[];
  routes: { service: string; route: string; requests: number; err_5xx: number; err_4xx: number; p50_ms: number; p95_ms: number; max_ms: number }[];
  targets: { target: string; calls: number; failed: number; p50_ms: number; p95_ms: number; max_ms: number }[];
  services: { service: string; instances: number; last_seen: string; requests: number; problems: number }[];
  recorders: { service: string; instance: string; at: string; counters: Record<string, number> }[];
}

export const api = {
  // World Radio (ADR 0100)
  radio: async (market: string) => {
    const d = await get<RadioOverview>("/admin/v1/radio/" + enc(market));
    return { ...d, personas: d.personas ?? [], bed_playlists: d.bed_playlists ?? [], spend_today: d.spend_today ?? [] };
  },
  radioQueue: async (market: string) => {
    const d = await get<{ market: string; now: number; items: RadioQueueItem[] | null }>("/admin/v1/radio/" + enc(market) + "/queue");
    return { ...d, items: d.items ?? [] };
  },
  radioSegments: async (market: string, kind?: string) =>
    ((await get<{ segments: RadioSegment[] | null }>("/admin/v1/radio/" + enc(market) + "/segments", { kind })).segments ?? []).map((s) => ({ ...s, facts: s.facts ?? [] })),
  radioSegmentAudio: (id: string, alt: boolean) => fetchBlob("/admin/v1/radio/segments/" + enc(id) + "/audio", alt ? { alt: 1 } : undefined),
  // Music Studio (ADR 0100)
  musicOverview: () => get<MusicOverview>("/admin/v1/music/overview"),
  musicTracks: async (q: { market?: string; status?: string; kind?: string; q?: string; limit?: number } = {}) => {
    const d = await get<{ tracks: MusicTrackRow[] | null }>("/admin/v1/music/tracks", q);
    return d.tracks ?? [];
  },
  musicTrack: async (id: string) => {
    const d = await get<MusicTrackDetail>("/admin/v1/music/tracks/" + enc(id));
    return { ...d, versions: d.versions ?? [], jobs: d.jobs ?? [] };
  },
  musicAudio: (versionId: string, alt: boolean) => fetchBlob("/admin/v1/music/versions/" + enc(versionId) + "/audio", alt ? { alt: 1 } : undefined),
  musicSpend: async () => {
    const d = await get<{ spend: { market: string; day: string; pool: string; micros: number; requests: number }[] | null; config: { daily_budget_usd: number } }>("/admin/v1/music/spend");
    return { ...d, spend: d.spend ?? [] };
  },
  musicPlaylists: async (market?: string) => (await get<{ playlists: MusicPlaylist[] | null }>("/admin/v1/music/playlists", { market })).playlists ?? [],
  musicPlacements: async (market?: string) => (await get<{ placements: MusicPlacement[] | null }>("/admin/v1/music/placements", { market })).placements ?? [],
  musicCues: async (market?: string) => (await get<{ cues: MusicCue[] | null }>("/admin/v1/music/cues", { market })).cues ?? [],
  musicTargets: (market: string) => get<MusicTargets>("/admin/v1/music/targets", { market }),


  // Sign-in and session (ADM-01)
  loginDev: (email: string) => post<LoginResult>("/admin/v1/auth/login", { provider: "dev", id_token: "dev:" + email.trim().toLowerCase() }),
  requestLoginCode: (email: string) => post<{ sent_to: string; expires_in_minutes: number }>("/admin/v1/auth/code", { email: email.trim() }),
  verifyLoginCode: (email: string, code: string) => post<LoginResult>("/admin/v1/auth/verify", { email: email.trim(), code: code.replace(/\s/g, "") }),
  passkeyLoginBegin: () => post<PasskeyCeremony>("/admin/v1/auth/passkey/begin"),
  passkeyLoginFinish: (ceremony: string, credential: unknown) => post<LoginResult>("/admin/v1/auth/passkey/finish", { ceremony, credential }),
  inviteInfo: (token: string) => get<InviteInfo>("/admin/v1/auth/invites/" + enc(token)),
  inviteBegin: (token: string) => post<PasskeyCeremony>("/admin/v1/auth/invites/" + enc(token) + "/begin"),
  /** Claims the account from the invite link; with a passkey from inviteBegin's ceremony, creates that too. */
  inviteAccept: (token: string, passkey?: { ceremony: string; credential: unknown; name?: string }) =>
    post<LoginResult>("/admin/v1/auth/invites/" + enc(token) + "/accept", passkey ?? {}),
  myPasskeys: () => get<{ passkeys: Passkey[]; rp_id: string }>("/admin/v1/me/passkeys"),
  myPasskeyBegin: () => post<PasskeyCeremony>("/admin/v1/me/passkeys/begin"),
  myPasskeyFinish: (ceremony: string, credential: unknown, name: string) => post<Passkey>("/admin/v1/me/passkeys/finish", { ceremony, credential, name }),
  deleteMyPasskey: (id: string) => del<{ ok: boolean }>("/admin/v1/me/passkeys/" + enc(id)),
  logout: () => post<{ ok: boolean }>("/admin/v1/auth/logout"),
  me: () => get<Me>("/admin/v1/me"),

  overview: (market?: string) =>
    get<{ tiles: Row; markets: string[]; revenue?: RevenueRow[]; providers?: Provider[] }>("/admin/v1/overview", { market }),

  // Players (ADM-11)
  players: (q: string) => get<{ players: PlayerRow[] }>("/admin/v1/players", { q }),
  player: async (id: string) => {
    // A failing sub-query comes back as null instead of [] (see BACKEND_ISSUES.md), so normalise lists.
    const p = await get<PlayerProfile>("/admin/v1/players/" + enc(id));
    const lists = ["identities", "sessions", "devices", "assets", "parties", "badges", "notes", "flags", "risk_signals", "tickets", "onboarding"] as const;
    for (const k of lists) if (!Array.isArray(p[k])) (p as unknown as Record<string, unknown>)[k] = [];
    return p;
  },
  ledger: (id: string, before?: string) => get<Ledger>("/admin/v1/players/" + enc(id) + "/ledger", { before }),
  playerWork: async (id: string) => {
    const w = await get<PlayerWork>("/admin/v1/players/" + enc(id) + "/work");
    for (const k of ["employment", "shifts", "payslips", "experience"] as const) if (!Array.isArray(w[k])) (w as unknown as Record<string, unknown>)[k] = [];
    return w;
  },
  reveal: (id: string, reason: string) => post<{ dob: string | null; note: string }>("/admin/v1/players/" + enc(id) + "/reveal", { reason }),
  addNote: (id: string, body: string) => post<{ id: string }>("/admin/v1/players/" + enc(id) + "/notes", { body }),
  playerAction: (id: string, action: string, fields: Row, reason: string) =>
    post<ActionResult>("/admin/v1/players/" + enc(id) + "/actions/" + enc(action), { ...fields, reason }),

  // Generic actions and approvals (ADM-04)
  action: (name: string, payload: unknown, reason: string) => post<ActionResult>("/admin/v1/actions/" + enc(name), { payload, reason }),
  approvals: (status = "pending") => get<{ approvals: Approval[] }>("/admin/v1/approvals", { status }),
  decide: (id: string, decision: "approve" | "reject", note: string) =>
    post<{ status: string; result?: unknown }>("/admin/v1/approvals/" + enc(id), { decision, note }),

  // Audit (ADM-03)
  audit: (q: { action?: string; target?: string; staff?: string; before_seq?: number; limit?: number }) =>
    get<{ entries: AuditEntry[] }>("/admin/v1/audit", q),
  auditVerify: () => get<AuditVerifyResult>("/admin/v1/audit/verify"),
  auditAnchors: () => get<AuditAnchorReport>("/admin/v1/audit/anchors"),
  auditExport: (from?: string) => download("/admin/v1/audit/export.csv", `softlife-audit-${new Date().toISOString().slice(0, 10)}.csv`, { from }),

  // Payments and finance (ADM-12, 24, 25)
  orders: (q: { status?: string; provider?: string; market?: string; q?: string; account_id?: string; before?: string; limit?: number }) =>
    get<{ orders: OrderRow[] }>("/admin/v1/orders", q),
  order: async (id: string) => {
    // The detail view names the id `order_id` while the list uses `id` (see BACKEND_ISSUES.md).
    const d = await get<OrderDetail>("/admin/v1/orders/" + enc(id));
    const o = d.order as OrderDetail["order"] & { order_id?: string };
    if (!o.id && o.order_id) o.id = o.order_id;
    d.api_calls ??= [];
    d.disputes ??= [];
    return d;
  },
  refund: (id: string, amountMinor: number, reason: string, on?: { ticketId?: string; voluntary?: boolean }) =>
    post<ActionResult>("/admin/v1/orders/" + enc(id) + "/refund", { amount_minor: amountMinor, reason, ticket_id: on?.ticketId || undefined, voluntary: on?.voluntary || undefined }),
  redeliver: (id: string, reason: string, ticketId?: string) =>
    post<ActionResult>("/admin/v1/orders/" + enc(id) + "/redeliver", { reason, ticket_id: ticketId || undefined }),
  journal: (q: { from?: string; to?: string; account?: string; limit?: number }) => get<{ lines: JournalLine[] }>("/admin/v1/finance/journal", q),
  journalCsv: (q: { from?: string; to?: string; account?: string }) => download("/admin/v1/finance/export/journal.csv", "softlife-journal.csv", q),
  trialBalance: () =>
    get<{ accounts: { account_code: string; currency: string; debit_minor: number; credit_minor: number; balance_minor: number }[] }>(
      "/admin/v1/finance/trial-balance",
    ),
  revenue: (q: { from?: string; to?: string; by?: "day" | "" }) => get<{ from: ISO; to: ISO; rows: RevenueRow[] }>("/admin/v1/finance/revenue", q),
  settlements: () =>
    get<{
      settlements: { id: string; provider: string; settlement_date: ISO; provider_ref: string; gross_minor: number; fees_minor: number; net_minor: number; currency: string; imported_at: ISO }[];
    }>("/admin/v1/finance/settlements"),
  importSettlement: (body: SettlementImport) =>
    post<{ settlement_id: string; gross_minor: number; fees_minor: number; net_minor: number; results: Record<string, number> }>(
      "/admin/v1/finance/settlements",
      body,
    ),
  recon: (all: boolean) =>
    get<{
      results: { id: string; settlement_id: string; order_id: string | null; provider_ref: string; kind: string; detail: Row; resolved_by: string | null; resolved_at: ISO | null; created_at: ISO }[];
    }>("/admin/v1/finance/recon", { all: all ? "true" : undefined }),
  resolveRecon: (id: string, note: string) => post<{ ok: boolean }>("/admin/v1/finance/recon/" + enc(id) + "/resolve", { note }),
  disputes: (status?: string) =>
    get<{
      disputes: { id: string; order_id: string; account_id: string; market: string; provider: string; amount_minor: number; currency: string; status: string; deadline: ISO | null; reason: string | null; created_at: ISO; ticket_id?: string; ticket_ref?: string }[];
    }>("/admin/v1/finance/disputes", { status }),
  disputeUpdate: (id: string, action: "evidence" | "accept", evidence?: unknown) =>
    post<{ status: string }>("/admin/v1/finance/disputes/" + enc(id), { action, evidence }),
  liability: () =>
    get<{ snapshots: { day: ISO; market: string; purchased_play: number; outstanding_play: number; outstanding_minor: number; currency: string }[] }>(
      "/admin/v1/finance/liability",
    ),
  providers: () => get<{ providers: Provider[] }>("/admin/v1/finance/providers"),
  outbox: (status = "dead") =>
    get<{ calls: { id: string; order_id: string; action: string; seq: number; status: string; attempts: number; last_error: string | null; next_at: ISO; created_at: ISO }[] }>(
      "/admin/v1/finance/outbox",
      { status },
    ),

  // Economy (ADM-13)
  catVersions: (m: string) => get<{ versions: CatalogueVersionRow[] }>(`/admin/v1/catalogue/${enc(m)}/versions`),
  catVersion: (m: string, v: number) => get<CatalogueVersion>(`/admin/v1/catalogue/${enc(m)}/versions/${v}`),
  catDiff: (m: string, from: number, to: number) => get<{ from: number; to: number; changes: DiffChange[] }>(`/admin/v1/catalogue/${enc(m)}/diff`, { from, to }),
  catDraft: (m: string, body: unknown, notes: string) => post<DraftResult>(`/admin/v1/catalogue/${enc(m)}/drafts`, { body, notes }),
  catSubmit: (m: string, v: number) => post<{ status: string }>(`/admin/v1/catalogue/${enc(m)}/versions/${v}/submit`),
  catPublish: (m: string, v: number, reason: string) => post<ActionResult>(`/admin/v1/catalogue/${enc(m)}/versions/${v}/publish`, { reason }),
  economyHealth: (m: string, days = 14) => get<EconomyHealth>(`/admin/v1/economy/${enc(m)}/health`, { days }),
  freePlay: (m: string) => get<FreePlay>("/admin/v1/economy/free", { market: m }),
  realSignals: async (m: string) => {
    const s = await get<SignalsView>("/admin/v1/signals/" + enc(m));
    for (const k of ["weather", "holidays", "fixtures", "providers", "flags"] as const) if (!Array.isArray(s[k])) (s as unknown as Record<string, unknown>)[k] = [];
    return s;
  },
  // Player businesses (ADR 0021, 0024): econ.demand_override is an action.
  biz: async (m: string) => {
    const r = await get<BizView>("/admin/v1/biz", { market: m });
    return { ...r, businesses: r.businesses ?? [], demand: r.demand ?? [], override: r.override ?? null };
  },
  freePreview: (p: FreeProposal) => post<FreePreview>("/admin/v1/economy/free/preview", p),
  freeDraft: (p: FreeProposal) => post<FreeDrafted>("/admin/v1/economy/free/draft", p),
  // World tuning (ADR 0059): arms prices and mission, incident and service rewards per market; same draft flow as free play.
  worldTuning: (m: string) => get<WorldTuning>("/admin/v1/economy/tuning", { market: m }),
  tuningPreview: (p: TuningProposal) => post<TuningPreview>("/admin/v1/economy/tuning/preview", p),
  tuningDraft: (p: TuningProposal) => post<TuningDrafted>("/admin/v1/economy/tuning/draft", p),
  // Business and P2P dials (ECN-14a): same draft flow as free play.
  bizEconomy: async (m: string) => {
    const r = await get<BizEconomy>("/admin/v1/economy/biz", { market: m });
    return { ...r, preview: bizEconPv(r.preview) };
  },
  bizEconPreview: async (p: BizEconProposal) => bizEconPv(await post<BizEconPreview>("/admin/v1/economy/biz/preview", p)),
  bizEconDraft: async (p: BizEconProposal) => {
    const r = await post<BizEconDrafted>("/admin/v1/economy/biz/draft", p);
    return { ...r, preview: bizEconPv(r.preview) };
  },

  // Live-ops (ADM-14, 28)
  flags: () => get<{ flags: Flag[] }>("/admin/v1/flags"),
  // risk:* and econ:p2p* flags run as actions: 200 {status: done} or 202 {status: pending_approval}.
  setFlag: (key: string, value: unknown, reason: string) => put<{ key: string; value: unknown } | ActionResult>("/admin/v1/flags/" + enc(key), { value, reason }),
  clearFlag: (key: string, reason: string) => del<{ ok: boolean } | ActionResult>("/admin/v1/flags/" + enc(key), { reason }),
  switches: () => get<SwitchList>("/admin/v1/switches"),
  // The same rules as the flags routes: 200 {status: done} or 202 {status: pending_approval}.
  setSwitch: (key: string, body: { state: SwitchState; market?: string; message?: string; reason: string }) =>
    put<SwitchResult>("/admin/v1/switches/" + enc(key), body),
  assistant: () => get<AssistantList>("/admin/v1/assistant/plans"),
  assistantAsk: (instruction: string, followUpTo?: string) =>
    post<AssistantPlan>("/admin/v1/assistant/plans", { instruction, follow_up_to: followUpTo || undefined }),
  assistantRun: (id: string, stepIds: string[]) =>
    post<{ status: AssistantPlanStatus; results: AssistantResult[]; plan: AssistantPlan }>("/admin/v1/assistant/plans/" + enc(id) + "/run", { step_ids: stepIds }),
  assistantDiscard: (id: string) => post<{ status: string }>("/admin/v1/assistant/plans/" + enc(id) + "/discard", {}),
  assistantSettings: () => get<AssistantSettingsView>("/admin/v1/assistant/settings"),
  assistantSettingsSave: (body: Record<string, unknown>, reason: string) => put<ActionResult>("/admin/v1/assistant/settings", { ...body, reason }),
  marketsLaunch: () => get<{ markets: MarketLaunch[] }>("/admin/v1/markets/launch"),
  pulse: (m: string) => get<{ days: PulseDay[]; events: PulseEvent[] | null }>(`/admin/v1/pulse/${enc(m)}`),
  schedulePulse: (m: string, day: string, eventKey: string, reason: string) =>
    put<{ day: string; event: PulseEvent }>(`/admin/v1/pulse/${enc(m)}/${enc(day)}`, { event_key: eventKey, reason }),
  index: (m: string) => get<IndexData>(`/admin/v1/index/${enc(m)}`),
  overrideIndex: (m: string, item: string, value: number | null, active: boolean, reason: string) =>
    put<ActionResult>(`/admin/v1/index/${enc(m)}/${enc(item)}`, { value, active, reason }),
  cups: (m: string) => get<{ cups: Cup[] }>(`/admin/v1/cups/${enc(m)}`),

  // Content CMS (ADM-15)
  contentVersions: (city: string) => get<{ versions: ContentVersionRow[] }>(`/admin/v1/content/${enc(city)}/versions`),
  contentVersion: (city: string, v: number) => get<ContentVersion>(`/admin/v1/content/${enc(city)}/versions/${v}`),
  contentDraft: (city: string, body: unknown) => post<{ version: number; lint: LintIssue[] | null }>(`/admin/v1/content/${enc(city)}/drafts`, { body }),
  contentSubmit: (city: string, v: number) => post<{ status: string }>(`/admin/v1/content/${enc(city)}/versions/${v}/submit`),
  contentPublish: (city: string, v: number) => post<{ status: string }>(`/admin/v1/content/${enc(city)}/versions/${v}/publish`),

  // Story sets (ADR 0026): same lifecycle as line sets, per market.
  storyVersions: (m: string) => get<{ versions: StoryVersionRow[] }>(`/admin/v1/story/${enc(m)}/versions`),
  storyVersion: (m: string, v: number) => get<StoryVersion>(`/admin/v1/story/${enc(m)}/versions/${v}`),
  storyCheck: (m: string, body: unknown) => post<StoryCheck>(`/admin/v1/story/${enc(m)}/check`, { body }),
  storyDraft: (m: string, body: unknown) => post<StoryCheck & { version: number }>(`/admin/v1/story/${enc(m)}/drafts`, { body }),
  storySubmit: (m: string, v: number) => post<{ status: string }>(`/admin/v1/story/${enc(m)}/versions/${v}/submit`),
  storyPublish: (m: string, v: number) => post<{ status: string }>(`/admin/v1/story/${enc(m)}/versions/${v}/publish`),

  // Courses and certificates (ADR 0023): the story lifecycle, but publishing is four-eyes (course.publish, 202 pending_approval).
  courses: (m: string) => get<{ market: string; courses: CourseRow[] }>(`/admin/v1/courses/${enc(m)}`),
  courseVersions: (m: string, c: string) => get<{ versions: CourseVersionRow[] }>(`/admin/v1/courses/${enc(m)}/${enc(c)}/versions`),
  courseVersion: (m: string, c: string, v: number) => get<CourseVersion>(`/admin/v1/courses/${enc(m)}/${enc(c)}/versions/${v}`),
  courseCheck: (m: string, c: string, body: unknown) => post<CourseCheck>(`/admin/v1/courses/${enc(m)}/${enc(c)}/check`, { body }),
  courseDraft: (m: string, c: string, body: unknown) => post<CourseCheck & { version: number }>(`/admin/v1/courses/${enc(m)}/${enc(c)}/drafts`, { body }),
  courseSubmit: (m: string, c: string, v: number) => post<{ status: string }>(`/admin/v1/courses/${enc(m)}/${enc(c)}/versions/${v}/submit`),
  coursePublish: (m: string, c: string, v: number, accuracyChecked: boolean, reason: string) =>
    post<ActionResult>(`/admin/v1/courses/${enc(m)}/${enc(c)}/versions/${v}/publish`, { reason, accuracy_checked: accuracyChecked }),
  courseStats: (m: string, c: string) => get<CourseStats>(`/admin/v1/courses/${enc(m)}/${enc(c)}/stats`),
  heldQuizzes: () => get<{ attempts: HeldQuiz[] }>("/admin/v1/quizzes/held"),
  clearQuiz: (attempt: string, clear: boolean, reason: string) => post<ActionResult>(`/admin/v1/quizzes/${enc(attempt)}/clear`, { clear, reason }),

  // World Director (ADR 0027): proposals are director.decide, envelopes director.envelope (four-eyes),
  // pauses are kill:director[:<kind>][:<MK>] flags.
  director: (m: string) => get<DirectorOverview>(`/admin/v1/director/${enc(m)}`),
  directives: async (m: string, q: { kind?: string; status?: string; limit?: number } = {}) => {
    const d = await get<{ directives: Directive[] | null }>(`/admin/v1/director/${enc(m)}/directives`, q);
    return { directives: d.directives ?? [] };
  },
  directorDryRun: async (m: string, days: number, envelope?: Record<string, EnvelopeBody>) => {
    const d = await post<DirectorDryRun>(`/admin/v1/director/${enc(m)}/dry-run`, envelope && Object.keys(envelope).length ? { days, envelope } : { days });
    return { ...d, directives: d.directives ?? [], summary: d.summary ?? {} };
  },
  playerHooks: async (id: string) => {
    const d = await get<DirectorHooks>(`/admin/v1/director/players/${enc(id)}/hooks`);
    return { ...d, hooks: d.hooks ?? [] };
  },
  directorDecide: (directiveId: string, decision: "approve" | "reject", reason: string) =>
    post<ActionResult>("/admin/v1/actions/director.decide", { payload: { directive_id: directiveId, decision }, reason }),
  directorEnvelope: (market: string, kind: string, body: EnvelopeBody, reason: string) =>
    post<ActionResult>("/admin/v1/actions/director.envelope", { payload: { market, kind, body }, reason }),
  // The economy autopilot (APL, LW5): verdicts and drafts (one person; a draft goes live only through
  // catalogue.publish, four-eyes), unfreezing a dial (four-eyes), autopublish on (four-eyes) or off.
  autopilot: async (m: string) => normalizeOverview(await get<AutopilotOverview>(`/admin/v1/director/${enc(m)}/autopilot`)),
  autopilotReports: async (m: string) => {
    const d = await get<{ reports: AutopilotReport[] | null }>(`/admin/v1/director/${enc(m)}/autopilot/reports`);
    return { reports: d.reports ?? [] };
  },
  autopilotVerdict: (proposalId: string, verdict: "agree" | "disagree", note: string, reason: string) =>
    post<ActionResult>("/admin/v1/actions/autopilot.verdict", { payload: { proposal_id: proposalId, verdict, note }, reason }),
  autopilotDraft: (proposalId: string, reason: string) => post<ActionResult>("/admin/v1/actions/autopilot.draft", { payload: { proposal_id: proposalId }, reason }),
  autopilotUnfreeze: (market: string, dial: string, reason: string) => post<ActionResult>("/admin/v1/actions/autopilot.unfreeze", { payload: { market, dial }, reason }),
  autopilotMode: (market: string, autopublish: boolean, reason: string) =>
    post<ActionResult>("/admin/v1/actions/autopilot.mode", { payload: { market, autopublish }, reason }),
  // Generated content (LW6): reviews (gen.review), levels (gen.level: raising is four-eyes), config
  // (gen.config, four-eyes); the market switch is the gen:<MK> flag, model calls stop with kill:gen[:<MK>].
  genOverview: (m: string) => get<GenOverview>(`/admin/v1/director/${enc(m)}/gen`),
  genContent: async (m: string, q: { kind?: string; status?: string; limit?: number } = {}) => {
    const d = await get<{ items: GenItem[] | null }>(`/admin/v1/director/${enc(m)}/content`, q);
    return { items: d.items ?? [] };
  },
  genItem: (m: string, id: string) => get<GenItemFull>(`/admin/v1/director/${enc(m)}/content/item/${enc(id)}`),
  genReview: (contentId: string, decision: "approve" | "reject", note: string, reason: string) =>
    post<ActionResult>("/admin/v1/actions/gen.review", { payload: { content_id: contentId, decision, note }, reason }),
  /** `from` is the level on screen: a request that waited for a second person doesn't undo a change made meanwhile. */
  genLevel: (market: string, kind: string, level: number, from: number, reason: string) =>
    post<ActionResult>("/admin/v1/actions/gen.level", { payload: { market, kind, level, from }, reason }),
  genConfig: (config: unknown, reason: string) => post<ActionResult>("/admin/v1/actions/gen.config", { payload: { config }, reason }),
  hookBandit: async (m: string) => {
    const d = await get<HookBandit>(`/admin/v1/director/${enc(m)}/bandit`);
    return { ...d, stats: d.stats ?? [], posterior: d.posterior ?? {} };
  },
  storyAIDraft: (m: string, body: AIDraftRequest) => post<AIDraftResult>(`/admin/v1/story/${enc(m)}/ai-draft`, body),

  // Social and moderation (ADM-16)
  crews: (q: string) => get<{ crews: Row[] }>("/admin/v1/crews", { q }),
  crew: (id: string) => get<{ crew: Row; ajo: Row[]; shared_devices: Row[] }>("/admin/v1/crews/" + enc(id)),
  parties: () => get<{ parties: Row[] }>("/admin/v1/parties"),
  party: (id: string) => get<{ party: Row; sprays: Row[] }>("/admin/v1/parties/" + enc(id)),
  reports: (status = "open") => get<{ reports: Row[] }>("/admin/v1/reports", { status }),
  resolveReport: (id: string, outcome: "dismissed" | "actioned", note: string) =>
    post<{ status: string }>("/admin/v1/reports/" + enc(id) + "/resolve", { outcome, note }),
  // Co-op ops (ADR 0025); cancelling a run is the ops.cancel_run action.
  ops: async (q: { status?: string; op?: string }) => {
    const d = await get<OpsView>("/admin/v1/ops", q);
    for (const k of ["runs", "summary", "fast_signals", "plans"] as const) if (!Array.isArray(d[k])) (d as unknown as Record<string, unknown>)[k] = [];
    for (const p of d.plans) if (!Array.isArray(p.setups_done)) p.setups_done = [];
    return d;
  },

  // Fraud (ADM-17)
  signals: (kind?: string, all?: boolean) =>
    get<{ signals: Row[]; last_24h: { kind: string; n: number; accounts: number }[] }>("/admin/v1/fraud/signals", { kind, all: all ? "true" : undefined }),
  cases: () => get<{ cases: Row[] }>("/admin/v1/fraud/cases"),
  openCase: (accountId: string, title: string, signalIds: string[]) =>
    post<{ id: string }>("/admin/v1/fraud/cases", { account_id: accountId, title, signal_ids: signalIds }),
  closeCase: (id: string, status: "actioned" | "false_positive" | "closed", outcome: string) =>
    post<{ status: string }>("/admin/v1/fraud/cases/" + enc(id) + "/close", { status, outcome }),
  // Table games' modes, rewards and caps (ADR 0037)
  gameModes: () => get<{ modes: Row[]; games: string[]; daily_pct: number; stakes: { switches: Record<string, boolean>; max_stake_pct: number; min_rake_pct: number; max_rake_pct: number; loss_day_pct: number; house_games?: string[]; house_win_day_pct?: number; house_max_multiple?: number }; house?: Row[]; audience: { rows: Row[]; defaults: Record<string, "everyone" | "adults"> } }>("/admin/v1/games/modes"),
  // Who may sit at a table game (ADR 0107): everyone, verified adults, or back to the default.
  setGameAudience: (game: string, market: string, audience: "everyone" | "adults" | "default", reason: string) =>
    put<ActionResult>("/admin/v1/games/audience/" + enc(game), { market, audience, reason }),
  setGameMode: (game: string, m: { market: string; mode: string; win_pct: number; draw_pct: number; xp_win: number; xp_draw: number; daily_cap_pct: number; min_moves: number; stake_pct?: number; rake_pct?: number }, reason: string) =>
    put<ActionResult>("/admin/v1/games/modes/" + enc(game), { ...m, reason }),
  rules: () => get<{ rules: Row[] }>("/admin/v1/fraud/rules"),
  setRule: (key: string, market: string, threshold: number, enabled: boolean, reason: string) =>
    put<ActionResult>("/admin/v1/fraud/rules/" + enc(key), { market, threshold, enabled, reason }),

  // Device graph and welcome-gift holds (WAL-09)
  clusters: async (q: { min_size?: number; review?: ClusterReview | "" }) => {
    const r = await get<{ clusters: DeviceCluster[] | null; held_gifts: number; threshold: number }>("/admin/v1/fraud/clusters", q);
    return { ...r, clusters: r.clusters ?? [] };
  },
  cluster: async (root: string) => {
    const r = await get<{ cluster: DeviceCluster; members: ClusterMember[] | null }>("/admin/v1/fraud/clusters/" + enc(root));
    return { ...r, members: r.members ?? [] };
  },
  welcomeHolds: async (status: HoldStatus = "held") => {
    const r = await get<{ holds: WelcomeHold[] | null }>("/admin/v1/fraud/welcome-holds", { status });
    return { holds: r.holds ?? [] };
  },

  // Support, privacy, spend reviews (ADM-20, 30, 32)
  tickets: (q: TicketFilter = {}) => get<TicketQueue>("/admin/v1/tickets", { ...q }),
  ticket: (id: string) => get<TicketDetail>("/admin/v1/tickets/" + enc(id)),
  feedback: (q: { status?: string; kind?: string; q?: string } = {}) => get<FeedbackQueue>("/admin/v1/feedback", { ...q }),
  feedbackOne: (id: string) => get<FeedbackDetail>("/admin/v1/feedback/" + enc(id)),
  ticketMacros: () => get<{ macros: TicketMacro[] }>("/admin/v1/ticket-macros"),
  /** The evidence pack, built now (audited, never stored). */
  ticketEvidence: (id: string) => get<Row>("/admin/v1/tickets/" + enc(id) + "/evidence"),
  ticketEvidenceText: (id: string, ref: string) => download("/admin/v1/tickets/" + enc(id) + "/evidence", `softlife-evidence-${(ref || id.slice(0, 8)).toLowerCase()}.txt`, { format: "txt" }),
  /** Click to view a picture: audited, answers a two-minute address; the picture is then fetched with this session. */
  openTicketPicture: async (id: string, pictureId: string): Promise<{ blob: Blob; expires_at: ISO; content_type: string }> => {
    const o = await post<{ url: string; expires_at: ISO; content_type: string }>("/admin/v1/tickets/" + enc(id) + "/attachments/" + enc(pictureId) + "/open");
    const res = await raw("GET", o.url);
    return { blob: await res.blob(), expires_at: o.expires_at, content_type: o.content_type };
  },
  privacy: () => get<{ requests: Row[] }>("/admin/v1/privacy"),
  privacyExport: (id: string) => download("/admin/v1/privacy/" + enc(id) + "/export", `softlife-data-${id.slice(0, 8)}.json`),
  privacySendExport: (id: string) => post<{ export_id: string; sent_to: string; expires_at: ISO }>("/admin/v1/privacy/" + enc(id) + "/send-export", {}),
  privacyHold: (id: string, reason: string) => post<{ status: string }>("/admin/v1/privacy/" + enc(id) + "/hold", { reason }),
  privacyDelete: (id: string, reason: string) => post<ActionResult>("/admin/v1/privacy/" + enc(id) + "/delete", { reason }),
  spendReviews: () => get<{ reviews: Row[] }>("/admin/v1/spend-reviews"),
  reviewSpend: (id: string, status: "checked_in" | "caps_lowered" | "dismissed", note: string) =>
    post<{ status: string }>("/admin/v1/spend-reviews/" + enc(id), { status, note }),

  // Bulk compensation and messaging (ADM-27, 19)
  compPreview: (segment: Segment, amount: number) =>
    post<{ players: number; total_play: number; total_packs: number }>("/admin/v1/compensation/preview", { segment, amount }),
  compensate: (segment: Segment, amount: number, title: string, body: string, reason: string) =>
    post<ActionResult>("/admin/v1/compensation", { segment, amount, title, body, reason }),
  message: (segment: Segment, title: string, body: string, reason: string) => post<ActionResult>("/admin/v1/messages", { segment, title, body, reason }),

  // Sponsorship (ADM-18)
  brands: () => get<{ brands: Row[] }>("/admin/v1/brands"),
  createBrand: (name: string, category: string) => post<{ id: string }>("/admin/v1/brands", { name, category }),
  campaigns: async () => {
    const r = await get<{ campaigns: Campaign[] | null }>("/admin/v1/campaigns");
    return { campaigns: r.campaigns ?? [] };
  },
  createCampaign: (c: {
    brand_id: string;
    market: string;
    placement: string;
    item: unknown;
    buy_url: string;
    age_restricted: boolean;
    starts_at: string;
    ends_at: string;
    fee_minor: number;
    currency: string;
  }) => post<{ id: string }>("/admin/v1/campaigns", c),
  campaignStatus: (id: string, status: string) => post<{ status: string }>("/admin/v1/campaigns/" + enc(id) + "/status", { status }),

  // Sponsorship v2: placements, inventory, creatives, reports
  placements: () => get<{ placements: Placement[] }>("/admin/v1/ads/placements"),
  inventory: (market: string) => get<{ inventory: InventoryRow[] | null; fill: AdFillPlacement[] | null }>("/admin/v1/ads/inventory", { market }),
  creatives: (status: "pending" | "approved" | "rejected" = "pending") => get<{ creatives: Creative[] | null }>("/admin/v1/creatives", { status }),
  addCreative: (campaignId: string, placement: string, payload: CreativePayload, assetKey?: string) =>
    post<{ id: string; status: string }>("/admin/v1/campaigns/" + enc(campaignId) + "/creatives", { placement, payload, ...(assetKey ? { asset_key: assetKey } : {}) }),
  campaignReport: (id: string) => get<CampaignReport>("/admin/v1/campaigns/" + enc(id) + "/report"),
  campaignInvoice: (id: string) => download("/admin/v1/campaigns/" + enc(id) + "/invoice.csv", `softlife-campaign-${id.slice(0, 8)}-invoice.csv`),

  // Ads v3 (ADR 0017): overview, partners, billing, rate cards, creative images
  adsOverview: (days: 1 | 7 | 30) => get<AdsOverview>("/admin/v1/ads/overview", { days }),
  partners: async (status?: PartnerStatus) => {
    const r = await get<{ partners: PartnerRow[] | null }>("/admin/v1/partners", { status });
    return { partners: r.partners ?? [] };
  },
  partner: (id: string) => get<PartnerDetail>("/admin/v1/partners/" + enc(id)),
  partnerBilling: (id: string) => get<PartnerBilling>("/admin/v1/partners/" + enc(id) + "/billing"),
  /** A top-up receipt, printable, in a new tab (each view is audited). */
  openReceipt: (partnerId: string, invoiceId: string) => openInTab("/admin/v1/partners/" + enc(partnerId) + "/invoices/" + enc(invoiceId) + "/receipt"),
  /** The same receipt saved as a file: printable HTML, or one CSV row in minor units. */
  downloadReceipt: (partnerId: string, invoiceId: string, number: string | null, format: "html" | "csv") =>
    format === "csv"
      ? download("/admin/v1/partners/" + enc(partnerId) + "/invoices/" + enc(invoiceId) + "/receipt.csv", `softlife-receipt-${receiptName(number, invoiceId)}.csv`)
      : download("/admin/v1/partners/" + enc(partnerId) + "/invoices/" + enc(invoiceId) + "/receipt", `softlife-receipt-${receiptName(number, invoiceId)}.html`, { download: 1 }),
  adBalances: async () => {
    const r = await get<{ partners: BalanceRow[] | null }>("/admin/v1/ads/billing");
    return { partners: r.partners ?? [] };
  },
  rateCards: (market: string) => get<{ market: string; currency: string; rate_cards: RateCard[] | null }>("/admin/v1/rate-cards", { market }),
  uploadAsset: (placement: string, file: Blob) => post<AdAsset>("/admin/v1/ads/assets", file, { placement }),

  // Moderation: chat, guestbook, mutes, word lists
  chat: (q: { market: string; status?: ModStatus; channel?: string; account?: string }) => get<{ messages: ChatMessage[] | null }>("/admin/v1/chat", q),
  guestbook: (q: { market: string; status?: ModStatus }) => get<{ entries: GuestbookEntry[] | null }>("/admin/v1/guestbook", q),
  mutes: (market: string) => get<{ mutes: Mute[] | null }>("/admin/v1/mutes", { market }),
  words: (market: string) => get<{ words: Word[] | null }>("/admin/v1/words", { market }),

  // UGC moderation (ADR 0024): decisions are ugc.* actions.
  /** item: only the entries for one item (its id). */
  ugcQueue: async (q: { status?: UGCQueueStatus; market?: string; kind?: string; item?: string }) => {
    const r = await get<{ items: UGCQueueItem[] | null }>("/admin/v1/ugc/queue", q);
    return { items: r.items ?? [] };
  },
  ugcStrikes: async (accountId?: string) => {
    const r = await get<{ strikes: UGCStrike[] | null }>("/admin/v1/ugc/strikes", { account_id: accountId });
    return { strikes: r.strikes ?? [] };
  },
  ugcAppeals: async (status: AppealStatus = "open") => {
    const r = await get<{ appeals: UGCAppeal[] | null }>("/admin/v1/ugc/appeals", { status });
    return { appeals: r.appeals ?? [] };
  },
  ugcReserved: async (market?: string) => {
    const r = await get<{ names: ReservedName[] | null }>("/admin/v1/ugc/reserved", { market });
    return { names: r.names ?? [] };
  },
  /** The optional classifier (UGM-11): switches, evaluations and activity. Switching, config and evaluations are ugc.llm_* actions. */
  ugcClassifier: (market?: string) => get<UGCClassifier>("/admin/v1/ugc/classifier", { market }),
  /** Designed goods, newest first; status defaults to everything but drafts. Staff pick and featured slots are ugc.design_* actions. */
  ugcDesigns: async (q: { market?: string; status?: string; kind?: string; q?: string }) => {
    const r = await get<{ designs: UGCDesign[] | null }>("/admin/v1/ugc/designs", q);
    return { designs: r.designs ?? [] };
  },
  // Living City (M5): player events, city posts and reviews; queue decisions stay ugc.decide.
  ugcEvents: async (q: { market?: string; status?: string; q?: string }) => {
    const r = await get<{ events: CityEventRow[] | null }>("/admin/v1/ugc/events", q);
    return { events: r.events ?? [] };
  },
  ugcPosts: async (q: { market?: string; status?: string; q?: string }) => {
    const r = await get<{ posts: CityPostRow[] | null }>("/admin/v1/ugc/posts", q);
    return { posts: r.posts ?? [] };
  },
  ugcReviews: async (q: { market?: string; status?: string; target_kind?: string; target_id?: string; flagged?: boolean }) => {
    const r = await get<{ reviews: CityReviewRow[] | null }>("/admin/v1/ugc/reviews", { ...q, flagged: q.flagged ? "1" : undefined });
    return { reviews: r.reviews ?? [] };
  },
  // The City Times editorial (TMS-03): times.pick, times.headline, times.drop and times.rebuild are actions.
  times: async (q: { market: string; city?: string; day?: string }) => {
    const r = await get<{ editions: Edition[] | null; cities: TimesView["cities"] | null; on: boolean; players?: TimesView["players"] | null }>("/admin/v1/times", q);
    return { editions: r.editions ?? [], cities: r.cities ?? [], on: !!r.on, players: r.players ?? [] } satisfies TimesView;
  },
  // Civic projects (ECN-08a): civic.upsert (four-eyes when the unlock has effects) and civic.cancel are actions.
  civic: async (market: string) => {
    const r = await get<CivicView>("/admin/v1/civic", { market });
    return {
      ...r,
      projects: r.projects ?? [],
      city_week: r.city_week ?? [],
      cities: r.cities ?? [],
      landmarks: r.landmarks ?? [],
      districts: r.districts ?? [],
      event_kinds: r.event_kinds ?? [],
      props: r.props ?? [],
    } satisfies CivicView;
  },

  // Design contests (DSN-09, M7): contest.upsert, contest.cancel and contest.remove_entry are actions (ugc.editorial).
  contests: async (market: string) => {
    const r = await get<ContestsView>("/admin/v1/contests", { market });
    return { ...r, contests: r.contests ?? [], entries: r.entries ?? [], palette: r.palette ?? [], kinds: r.kinds ?? [] } satisfies ContestsView;
  },

  // Audio rooms
  rooms: (market: string, status: "live" | "ended" = "live") => get<{ rooms: AudioRoom[] | null; livekit: boolean }>("/admin/v1/rooms", { market, status }),
  room: (id: string) => get<RoomDetail>("/admin/v1/rooms/" + enc(id)),
  observeRoom: (id: string, reason: string) => post<{ url: string; token: string; expires_in: number }>("/admin/v1/rooms/" + enc(id) + "/observe", { reason }),
  djs: (market: string) => get<{ djs: DJ[] | null }>("/admin/v1/djs", { market }),

  // World live-ops
  world: (m: string) => get<WorldOverview>(`/admin/v1/world/${enc(m)}`),
  worldEvents: (m: string) => get<{ events: WorldEvent[] | null }>(`/admin/v1/world/${enc(m)}/events`),
  worldPolls: (m: string) => get<{ polls: Poll[] | null }>(`/admin/v1/world/${enc(m)}/polls`),
  worldShowcases: (m: string) => get<{ entries: ShowcaseEntry[] | null }>(`/admin/v1/world/${enc(m)}/showcases`),

  // Waitlist & leads (marketing site)
  waitlist: async (q: { kind?: string; status?: string }) => {
    const r = await get<{ entries: WaitlistEntry[] | null; counts: { kind: string; status: string; count: number }[] | null; emails_masked: boolean }>("/admin/v1/waitlist", q);
    return { entries: r.entries ?? [], counts: r.counts ?? [], emails_masked: r.emails_masked };
  },

  // Growth
  onboardingFunnel: (market: string, days: 7 | 30) => get<OnboardingFunnel>("/admin/v1/growth/onboarding", { market, days }),
  growth: (market: string) => get<Growth>("/admin/v1/growth", { market }),

  // Platform event log (ADR 0095)
  logs: (q: LogsQuery) => get<LogsPage>("/admin/v1/logs", q as Query),
  logEvent: (id: number, at?: string) => get<LogEvent>("/admin/v1/logs/" + id, { at }),
  logTrace: (rid: string) => get<{ request_id: string; rows: LogEvent[] }>("/admin/v1/logs/trace/" + enc(rid)),
  logStats: (window: string) => get<LogsStats>("/admin/v1/logs/stats", { window }),
  logsExport: (q: LogsQuery) => download("/admin/v1/logs/export.csv", `softlife-logs-${new Date().toISOString().slice(0, 10)}.csv`, { ...q, limit: undefined, before: undefined, after_id: undefined } as Query),
  logsSettings: () => get<LogsSettings>("/admin/v1/logs/settings"),
  saveLogsSettings: (config: LogsConfig, reason: string) => put<LogsSettings>("/admin/v1/logs/settings", { config, reason }),

  // System (ADM-22)
  system: () => get<SystemStatus>("/admin/v1/system"),
  integrations: () => get<Integrations>("/admin/v1/system/integrations"),
  rerun: (job: string, runKey: string, reason: string) => post<{ status: string }>("/admin/v1/system/jobs/" + enc(job) + "/rerun", { run_key: runKey, reason }),

  // Team (ADM-23)
  team: () => get<{ staff: TeamMember[]; roles: Record<string, string[]> }>("/admin/v1/team"),
  teamReview: () => get<{ generated_at: ISO; staff: { email: string; roles: Grants; permissions: Permissions }[] }>("/admin/v1/team/review"),
  invite: (email: string, name: string, roles: Grants, reason: string) => post<ActionResult>("/admin/v1/team", { email, name, roles, reason }),
  resendStaffInvite: (id: string) => post<{ ok: boolean }>("/admin/v1/team/" + enc(id) + "/invite"),
  setRoles: (id: string, roles: Grants, reason: string) => put<ActionResult>("/admin/v1/team/" + enc(id) + "/roles", { roles, reason }),
  disableStaff: (id: string, reason: string) => post<ActionResult>("/admin/v1/team/" + enc(id) + "/disable", { reason }),
};
